/**
 * API Client for DMARC Platform
 * Handles all communication with the backend API
 */

const API_BASE = import.meta.env.VITE_API_URL || "";

// Demo mode for marketing screenshots - bypasses API and returns mock data
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

// Import mock data for demo mode
import {
  mockDomains,
  mockAlerts,
  mockReports,
  mockSources,
  mockRemediationTasks,
  mockDashboardStats,
  mockRemediationStats,
  mockSourceStats,
  mockApexLinks,
  mockOrganization,
  mockAnalyticsData,
  mockTierPermissions,
} from "./mockData";

/**
 * Get the full URL for auth endpoints (Google/GitHub OAuth)
 * In production, auth routes are on the API domain
 */
export function getAuthUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// Types
export interface User {
  id: string;
  organization_id: string;
  role: "owner" | "admin" | "member" | "readonly";
  email: string;
  name: string;
}

export interface Organization {
  id: string;
  name: string;
  billing_tier: "free" | "starter" | "pro" | "enterprise" | "enterprise_plus";
  tier?: string;
  industry_vertical?: string;
  company_size?: string;
  onboarding_completed?: boolean;
  // MSP fields
  organization_type?: "standard" | "msp" | "msp_client";
  parent_organization_id?: string | null;
  // MSP Partner tier - determines features and Verkh wholesale pricing
  partner_tier?: "reseller" | "white_label" | null;
  verkh_wholesale_cents_per_client?: number | null;
  // Stores the plan selected during signup (before payment) - used to redirect to checkout after onboarding
  signup_selected_plan?: "starter" | "pro" | "enterprise" | "enterprise_plus" | null;
  domain_count?: number;
  // Logo (Enterprise+ only)
  logo_url?: string;
  // Billing status fields
  billing_status?: "none" | "active" | "past_due" | "canceled" | "trialing" | "requires_action";
  billing_method?: "card" | "invoice";
  payment_terms?: "immediate" | "net15" | "net30";
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  cancel_effective_at?: string;
  downgrade_scheduled_tier?: string;
  downgrade_effective_at?: string;
  failed_payment_count?: number;
  last_payment_failure_at?: string;
  service_activated_at?: string;
  late_fee_start_date?: string;
  payment_method_brand?: string;
  payment_method_last4?: string;
  stripe_subscription_id?: string;
  // Billing address fields
  billing_name?: string;
  billing_email?: string;
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_postal_code?: string;
  billing_country?: string;
  tax_id?: string;
  tax_id_type?: "vat" | "gst" | "ein" | "abn" | "other";
  created_at: string;
}

export interface Domain {
  id: string;
  domain_name: string;
  verification_status: "pending" | "verified";
  verification_token: string;
  policy_goal: "none" | "quarantine" | "reject";
  monitoring_status: "active" | "paused" | "archived";
  current_policy?: string;
  current_dmarc?: string;
  dmarc_policy?: string;
  spf_configured?: boolean;
  dkim_configured?: boolean;
  pass_rate?: number;
  recent_pass_rate?: number;
  total_messages?: number;
  created_at: string;
  // Verification expiry fields for unverified domains
  verification_expires_at?: string;
  days_until_expiry?: number;
}

export interface Alert {
  id: string;
  domain_id: string;
  domain_name: string;
  alert_type: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  message?: string;
  status: "open" | "acknowledged" | "resolved" | "snoozed";
  acknowledged?: boolean;
  resolved?: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  resolved_at?: string;
}

// BIMI Types
export type BimiCheckStatus = "pass" | "fail" | "unknown";

export interface BimiReadinessCheck {
  id: string;
  label: string;
  description: string;
  status: BimiCheckStatus;
  passed: boolean;
}

export interface BimiReadinessResponse {
  domain_id: string;
  domain_name: string;
  /** Every requirement satisfied — not just the DMARC prerequisite. */
  is_ready: boolean;
  /** Percentage of all requirements confirmed passing; unverified counts as not passed. */
  score: number;
  checks: BimiReadinessCheck[];
  /** A bare percentage cannot say whether requirements failed or were never checked. */
  checks_passed: number;
  checks_total: number;
  checks_unverified: number;
  checked_at: string;
}

export interface BimiLogoValidationCheck {
  name: string;
  passed: boolean;
  message: string;
}

export interface BimiLogoValidationResult {
  is_valid: boolean;
  logo_url: string;
  checks: BimiLogoValidationCheck[];
  preview_available: boolean;
}

export interface BimiRecordGeneratorResponse {
  host: string;
  value: string;
  record_type: "TXT";
  selector: string;
  domain_name: string;
}

export interface Source {
  id: string;
  domain_id?: string;
  domain_name?: string;
  identifier: string;
  identifier_type: "ip" | "hostname";
  source_ip?: string;
  ptr_hostname?: string;
  provider_name?: string;
  source_name?: string;
  org_name?: string;
  authorization_status: "authorized" | "unauthorized" | "pending_review" | "ignored";
  approved?: boolean;
  total_message_count: number;
  pass_count?: number;
  fail_count?: number;
  // Alignment pass rates (DMARC requirement - does domain align with header_from?)
  spf_pass_rate?: number;
  dkim_pass_rate?: number;
  // Auth pass rates (raw SPF/DKIM check passed, regardless of alignment)
  spf_auth_pass_rate?: number;
  dkim_auth_pass_rate?: number;
  // Mail From domain (envelope_from) - helps identify what the source claims to be
  common_envelope_from?: string;
  first_seen_at: string;
  last_seen_at: string;
  // Labeling fields
  label_category?: string;
  label_subtype?: string;
  labeled_at?: string;
  // Forwarder classification
  source_type?: "direct" | "forwarder" | "mailing_list" | "trusted_forwarder" | "arc_forwarder" | "unknown";
  forwarding_detected?: boolean;
  // AI Classification fields
  ai_classification?: string;
  ai_confidence?: "high" | "medium" | "low";
  ai_reasoning?: string;
  ai_analyzed_at?: string;
  auto_labeled?: boolean;
  user_confirmed?: boolean;
  // Provider matching (from known_providers FK)
  matched_provider_id?: string;
  known_provider_name?: string;
  known_provider_category?: string;
  // Tier restriction flag
  _tier_restricted?: boolean;
}

export interface SourcesResponse {
  sources: Source[];
  tier: string;
  permissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
    canApplyRemediation: boolean;
  };
}

// ---------------------------------------------------------------------------
// Sources Spine — new-spine read types (Phase 1 PR 8+).
//
// These accompany the new `sources` table the matcher populates.
// Distinct from the legacy `Source` type above, which models the
// `sending_sources` table. Phase 1 PR 11 cuts the rest of /api/sources
// over; PR 19 drops `sending_sources` entirely.
// ---------------------------------------------------------------------------

/** Classification per sources-spine-design.md § "State Model". */
export type SpineClassification = "Unclassified" | "Approved" | "Forwarder" | "Not-Mine";
/** Health verdict; null means "no value written" (e.g. Unclassified). */
export type SpineHealth = "Aligned" | "Misconfigured" | "Insufficient-Data" | null;

/**
 * Differential-diagnosis recommendation attached to every spine
 * Source by the API (Phase 1 PR 17). Computed server-side via the
 * @dmarc-platform/shared `computeRecommendation` helper, mirrored to
 * the client so consumers don't need a dependency on @shared.
 */
export type SpineRecommendationKind =
  | "classify"
  | "fix_alignment"
  | "complete_esp_authentication"
  | "forwarder_expected"
  | "matcher_review"
  | "awaiting_data"
  | "resolved"
  | "none";

/**
 * Matcher rule that claimed this Source's observations. Mirrors the
 * `MatcherRuleId` union in @dmarc-platform/shared. Drives the
 * `complete_esp_authentication` branch of `SpineRecommendationKind`
 * (PR 29): when matchRule is `spf-only-fallback` on an ESP, SPF is
 * already passing against the ESP's own domain and the right
 * remediation lives in the ESP admin, not in the customer's SPF
 * record.
 */
export type SpineMatcherRuleId =
  | "known-provider-aligned"
  | "spf-only-fallback"
  | "synthetic-fallback";

export type SpineRecommendationSeverity =
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "info";

export interface SpineRecommendation {
  severity: SpineRecommendationSeverity;
  /** Short imperative line shown on compact /sources rows. */
  headline: string;
  /** 1-2 sentence detail rendered on the source detail page. */
  detail: string;
  /** Discriminator for branching UI (e.g. show "Classify" inline control). */
  kind: SpineRecommendationKind;
}

export interface SpineSource {
  id: string;
  domainId: string;
  /** Joined for convenience; the detail page renders it in the header. */
  domainName: string;
  /** 'rollout' | 'operate'. Frames per-source copy + mode-conditional UI. */
  domainMode: string;
  /** Null for synthetic-fallback Sources (the matcher couldn't identify a provider). */
  knownProviderId: string | null;
  knownProviderName: string | null;
  knownProviderCategory: string | null;
  /** Aligned DKIM d= or SPF MAIL FROM org-domain; null on synthetic Sources. */
  authIdentity: string | null;
  /** sha256-derived id for synthetic-fallback Sources; null on keyed Sources. */
  syntheticId: string | null;
  asn: string | null;
  cidrBucket: string | null;
  classification: SpineClassification;
  /** "Discovered" in the UI = classification 'Unclassified' AND confidencePinned. */
  confidencePinned: boolean;
  ownerUserId: string | null;
  /** Hydrated owner triple (PR 20). Null when unassigned or when the
   * user row was deleted. `ownerUserId` stays populated even if the
   * user row is gone — the LEFT JOIN just nulls the name/email pair. */
  owner: SpineSourceOwner | null;
  dismissedAt: string | null;
  health: SpineHealth;
  healthDmarcPassRate: number | null;
  healthMessageCount: number | null;
  healthWindowDays: number | null;
  healthEvaluatedAt: string | null;
  notes: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Matcher rule that produced this Source (PR 29). Null on rows that
   * pre-date the matcher rerun or that still lack a match record. */
  matchRule: SpineMatcherRuleId | null;
  /** Differential-diagnosis recommendation (PR 17). */
  recommendation: SpineRecommendation;
  /**
   * PR 54 — third-party vendor identified by reverse-DNS PTR of the
   * source's sending IP. Same rules as SpineSourceListRow.vendorBrandedDomain.
   */
  vendorBrandedDomain: string | null;
}

/**
 * Hydrated owner triple surfaced on every spine source response (PR 20).
 * `name` is optional because the legacy users.name column allows NULL —
 * the assignee picker falls back to email in that case.
 */
export interface SpineSourceOwner {
  id: string;
  name: string | null;
  email: string;
}

/**
 * Org user surfaced by the `/api/sources/owners` dropdown endpoint
 * (PR 20). Carries `role` so the dropdown can hide owners from
 * `readonly` users (or render a "viewer" badge) without a second fetch.
 */
export interface SpineOwnerCandidate {
  id: string;
  name: string | null;
  email: string;
  role: string;
}

export interface SpineOwnerListResponse {
  users: SpineOwnerCandidate[];
}

/**
 * Phase 2 PR 21 — Source comment row + history event row + responses.
 *
 * Author / actor mirror the SpineSourceOwner shape so render code can
 * reuse the same avatar + display-name helpers across all three places
 * where a user is hydrated (owner, comment author, activity actor).
 */
export interface SpineCommentAuthor {
  id: string;
  name: string | null;
  email: string;
}

export interface SpineSourceComment {
  id: string;
  sourceId: string;
  body: string;
  /** Null when the user row was deleted (ON DELETE SET NULL on author_user_id). */
  author: SpineCommentAuthor | null;
  createdAt: string;
  updatedAt: string;
}

export interface SpineSourceCommentsResponse {
  comments: SpineSourceComment[];
}

/**
 * Allowed event types in the per-Source activity log. Mirrors the
 * CHECK constraint in migration 0086 + the
 * `@dmarc-platform/shared/activity-log/events` taxonomy. New event
 * types land here AND in the renderer switch on the History tab.
 */
export type SpineActivityEventType =
  | "classification_changed"
  | "dismissed"
  | "undismissed"
  | "owner_changed"
  | "merged_into"
  | "split_from"
  | "comment_added"
  | "comment_edited"
  | "comment_deleted"
  | "ai_suggested"
  | "matcher_review_resolved";

export interface SpineSourceHistoryEvent {
  id: string;
  eventType: SpineActivityEventType;
  /** Null for system-generated events (matcher, AI suggester). */
  actor: SpineCommentAuthor | null;
  /**
   * Event-specific payload. Schema lives in
   * `@dmarc-platform/shared/activity-log/events`; the History tab's
   * renderer narrows by `eventType` and pulls the fields it needs.
   * Typed as `unknown` here so the renderer is the only place that
   * has to know the payload shape — the API surface stays generic.
   */
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface SpineSourceHistoryResponse {
  events: SpineSourceHistoryEvent[];
}

/**
 * Phase 3 PR 24 — Guided remediation per source.
 *
 * Three discriminated shapes via the `kind` field:
 *   - `guided`               — known provider; render records + steps
 *   - `needs_classification` — synthetic-fallback; classify first
 *   - `not_mine`             — user marked Not-Mine; nothing to do
 */
export interface SpineRemediationRecord {
  type: "spf" | "dkim";
  title: string;
  description: string;
  host: string;
  currentValue: string | null;
  recommendedValue: string;
  recordType: "TXT" | "CNAME";
  steps?: string[];
  note?: string;
  /**
   * PR 55 — live-state for the record. 'needs-action' is the legacy
   * default; 'already-authorized' (SPF) and 'verified' (DKIM) flip
   * the card to green with a trace block and suppress the publish
   * controls — the work is already done.
   */
  state?: "needs-action" | "already-authorized" | "verified";
  /** Human-readable trace for non-action states. */
  trace?: string;
  /** Structured evidence backing the trace. */
  evidence?: {
    spfIncludeChain?: string[];
    dkimSignedDomain?: string;
    dkimSelector?: string;
    observationsWithPass?: number;
    windowLabel?: string;
  };
}

export interface SpineRemediationProvider {
  id: string;
  name: string;
  category: string | null;
  subCategory: string | null;
  logoUrl: string | null;
  documentationUrl: string | null;
  vendorDmarcDocs: string | null;
  /** 'automatic' | 'cname' | 'txt' | 'manual' | 'none' */
  dkimComplexity: string | null;
}

/**
 * PR 55 — wrapper-level health. When `state === "healthy"` the UI
 * suppresses the per-record cards and renders a single "no action
 * needed" banner. Otherwise the per-record rendering still runs;
 * individual cards may still be green via `record.state`.
 */
export interface SpineRemediationHealth {
  state: "healthy" | "needs-action";
  /** sources.health (e.g. "Aligned", "Misconfigured"). */
  sourceHealth: string | null;
  spfReachable: boolean;
  dkimVerified: boolean;
}

export type SpineSourceRemediationResponse =
  | {
      sourceId: string;
      kind: "guided";
      domainName: string;
      provider: SpineRemediationProvider;
      records: SpineRemediationRecord[];
      notes: string | null;
      health: SpineRemediationHealth;
    }
  | {
      sourceId: string;
      kind: "needs_classification";
      domainName: string;
      /** Best-available identity (auth_identity / cidr_bucket / synthetic_id). */
      identity: string | null;
      message: string;
      hint: string;
    }
  | {
      sourceId: string;
      kind: "not_mine";
      domainName: string;
      message: string;
    };

/**
 * Phase 3 PR 25 — DNS publish via connected provider.
 *
 * Pre-flight integration check + per-record publish result. The
 * Remediation tab reads the integration first to decide whether to
 * render an active "Publish via Cloudflare" / "Publish via GoDaddy"
 * button or a "Connect a DNS provider first" stub link.
 */
export interface SpineRemediationIntegration {
  id: string;
  providerType: string;
  providerName: string;
  /** True when this integration is scoped specifically to this domain (vs. org-wide fallback). */
  domainScoped: boolean;
  /**
   * Phase 3 PR 25.5 — which publish flow this integration supports:
   *   - 'api_direct'     → batch POST /api/sources/:id/remediation/publish
   *   - 'domain_connect' → per-record redirect to Cloudflare via signed URL
   * Drives whether the UI renders the batch publish button (GoDaddy)
   * or a Cloudflare-specific "Apply via Cloudflare" link per SPF card.
   */
  applyMethod: "api_direct" | "domain_connect";
}

export interface SpineRemediationIntegrationResponse {
  sourceId: string;
  domainName: string;
  integration: SpineRemediationIntegration | null;
}

export interface SpineRemediationPublishResult {
  host: string;
  recordType: "TXT" | "CNAME";
  status: "published" | "skipped" | "failed";
  operationType?: "create" | "update";
  /** dns_record_backups.id for the row that snapshot the prior value (UPDATEs only). */
  backupId?: string;
  /** Sanitised error message for failed records. */
  error?: string;
  /** Reason for skipped records (e.g. "placeholder value"). */
  skipReason?: string;
}

export interface SpineRemediationPublishResponse {
  sourceId: string;
  integration: {
    id: string;
    providerType: string;
    providerName: string;
  };
  results: SpineRemediationPublishResult[];
  summary: { published: number; failed: number; skipped: number };
}

/**
 * Phase 3 PR 26 — close-the-loop verification record per publish
 * attempt. The processor's health-job auto-flips status='pending'
 * to 'verified' on the next batch that reads the source as
 * Aligned; 'failed' is set by a daily auditor after 14 days
 * without verification.
 */
export interface SpineRemediationVerification {
  id: string;
  status: "pending" | "verified" | "failed" | "cancelled";
  triggeredBy: "publish_endpoint" | "domain_connect_redirect" | "manual";
  /** Hydrated user triple. Null when the user row was deleted. */
  triggeredByUser: { id: string; name: string | null; email: string } | null;
  /** Snapshot of records the user tried to publish. */
  expectedRecords: unknown[];
  initialClassification: string | null;
  initialHealth: string | null;
  triggeredAt: string;
  verifiedAt: string | null;
  failedAt: string | null;
  cancelledAt: string | null;
  failureReason: string | null;
}

export interface SpineSourceVerificationsResponse {
  verifications: SpineRemediationVerification[];
}

/**
 * SPF lookup-budget meter response (Sources Spine Phase 3 PR 28).
 *
 * Powers the meter on the Enforcement page and the pre-approve
 * callout in the Source classify modal. `currentLookupCount` may be
 * null when the ai-worker resolver is unavailable; the UI degrades
 * to a "couldn't read SPF" state in that case.
 *
 * `projections[]` carries every known-provider linked to a non-
 * Not-Mine non-dismissed Source on the domain, with a per-provider
 * "would adding this push you over?" answer.
 */
export interface SpfBudgetProjection {
  providerId: string;
  providerName: string;
  spfInclude: string;
  sourceCount: number;
  classifications: string[];
  alreadyIncluded: boolean;
  /** Conservative lower bound (+1 unless already included). */
  wouldAdd: number;
  projectedLookupCount: number | null;
  projectedStatus: "ok" | "warning" | "exceeded" | "unknown";
}

export interface SpfBudgetResponse {
  domainId: string;
  domainName: string;
  currentSpfRecord: string | null;
  currentLookupCount: number | null;
  maxLookupCount: number;
  warningThreshold: number;
  headroom: number | null;
  status: "ok" | "warning" | "exceeded" | "unknown";
  resolverError: string | null;
  projections: SpfBudgetProjection[];
}

/**
 * One per-Source DKIM key hygiene row (Phase 3 PR 27). Returned by
 * GET /api/sources/:id/dkim-keys and rendered by the DKIM hygiene
 * panel on the Source detail Overview tab.
 */
export type SpineDkimKeyStatus =
  | "aligned"
  | "weak"
  | "revoked"
  | "missing"
  | "surprise";

export interface SpineDkimKeyObservation {
  id: string;
  /** DKIM `d=` — the signing domain that owns the key. */
  signingDomain: string;
  /** DKIM `s=` — the selector under `_domainkey.<signingDomain>`. */
  selector: string;
  keyType: string | null;
  keyBits: number | null;
  keyHash: string | null;
  status: SpineDkimKeyStatus;
  /** Total signed messages observed for this pair in the trailing window. */
  observationCount: number;
  firstSeen: string;
  lastSeen: string;
  lastCheckedAt: string | null;
}

export interface SpineSourceDkimKeysResponse {
  sourceId: string;
  domainName: string;
  keys: SpineDkimKeyObservation[];
}

/**
 * One per-Source ESP-subuser observation (Sources Spine Tier-3 PR 41).
 * Returned by GET /api/sources/:id/subuser-evidence. Captures the
 * subuser id revealed by walking a DKIM CNAME chain, plus aggregate-
 * only cross-tenant collision data.
 */
export interface SpineSubuserObservation {
  /** DKIM `d=` whose chain revealed the subuser. */
  signingDomain: string;
  selector: string;
  /** ID from `known_providers` (e.g. `provider_sendgrid`). */
  knownProviderId: string;
  /** ESP-side subuser identifier extracted from the chain. */
  subuserId: string;
  /** Provider whitelabel-pool suffix (e.g. `wl033.sendgrid.net`). */
  whitelabelHost: string | null;
  /** CNAME chain captured at resolution time, root → tip. */
  cnameChain: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  /**
   * Aggregate-only cross-tenant collision count. Zero is the common
   * case; non-zero indicates the same subuser is also observed by
   * other organizations — never reveals which orgs.
   */
  otherOrgsObservingSubuser: number;
  /** True iff the calling org was the first to register this subuser globally. */
  isFirstSeenByThisOrg: boolean;
}

export interface SpineSourceSubuserEvidenceResponse {
  sourceId: string;
  domainName: string;
  subuserObservations: SpineSubuserObservation[];
}

/**
 * One per-(d, selector) DKIM selector-collision row (Sources Spine
 * Tier-3 PR 42). Returned by GET /api/sources/:id/dkim-collisions.
 */
export interface SpineDkimCollisionPair {
  signingDomain: string;
  selector: string;
  /**
   * Other Sources within the caller's org that sign with the same
   * (signingDomain, selector). Owned by the caller — identifiers
   * returned.
   */
  intraOrgCollisions: Array<{
    sourceId: string;
    sourceDisplay: string;
    /** Subuser id known for the colliding Source (PR 41), null if not resolved yet. */
    subuserId: string | null;
  }>;
  /**
   * COUNT(DISTINCT organization_id) of OTHER orgs whose sources
   * signed the same (d, selector). Aggregate only — never identifiers.
   */
  crossOrgObservers: number;
  /**
   * COUNT(DISTINCT organization_id) of OTHER orgs whose sources
   * attributed the same (d, selector) to a DIFFERENT subuser id than
   * this Source. The strong DNS-collision signal. Aggregate only.
   */
  crossOrgSubuserCollisions: number;
}

export interface SpineSourceDkimCollisionsResponse {
  sourceId: string;
  domainName: string;
  pairs: SpineDkimCollisionPair[];
}

/**
 * One cross-source remediation lead (Sources Spine Tier-3 PR 43).
 * A sibling Source within the caller's org that's working — either on
 * the same domain or sharing the same known_provider.
 */
export interface SpineRemediationLinkageEntry {
  sourceId: string;
  sourceDisplay: string;
  knownProviderId: string | null;
  providerName: string | null;
  authIdentity: string | null;
  /**
   * Set only for sameProviderWorkingSources rows (sibling on a
   * DIFFERENT domain). Null for same-domain rows.
   */
  domainName: string | null;
  totalMessages: number;
  /** 0–1 fraction. Null when totalMessages is zero. */
  dmarcPassRate: number | null;
  lastSeen: string;
}

export interface SpineSourceRemediationLinkageResponse {
  sourceId: string;
  domainName: string;
  windowDays: number;
  /** 0–1 fraction, e.g. 0.95. */
  healthyThreshold: number;
  sameDomainWorkingSources: SpineRemediationLinkageEntry[];
  sameProviderWorkingSources: SpineRemediationLinkageEntry[];
}

// ============================================
// Public APEX share — domain dashboard vs source mirror
// (APEX A1, PR 45)
// ============================================

/**
 * The legacy public share dashboard payload — what GET /api/share/:token
 * returns when the link was created with the (default) domain scope.
 * Shape preserved verbatim from the prior inline return type so
 * ShareDashboard's existing components stay binary-compatible.
 */
export interface PublicApexLegacyDashboardResponse {
  /** Source-mirror discriminator. Absent / undefined on this legacy
   *  shape; present and equal to `"source_mirror"` on the new path. */
  kind?: undefined;
  domain: { name: string; pass_rate: number; policy: string };
  stats: { total_messages: number; pass_rate: number; spf_pass_rate: number; dkim_pass_rate: number };
  breakdown: Array<{ result: string; count: number }>;
  sources: Array<{
    id?: string;
    provider: string;
    identifier?: string;
    ptr_hostname?: string | null;
    source_type?: string | null;
    forwarding_detected?: boolean;
    label_category?: string | null;
    count: number;
    status: string;
    spf_pass_rate?: number;
    dkim_pass_rate?: number;
    issue?: string;
    fix?: string;
    is_expected?: boolean;
  }>;
  trend: Array<{ date: string; pass_rate: number; total_messages?: number }>;
  tasks: Array<{
    id: string;
    task_type: string;
    title: string;
    description: string;
    priority: string;
    status: string;
    suggested_record: string | null;
  }>;
  insights: Array<{
    id: string;
    type: string;
    severity: string;
    title: string;
    description: string;
    recommendations: string[];
    /** Optional partner-facing copy. Rendered above the generic
     *  recommendations when present. */
    partner_handoff?: string | null;
    confidence: string;
    created_at: string;
  }>;
  branding: {
    logo_url: string | null;
    company_name: string | null;
    primary_color: string | null;
    accent_color: string | null;
  } | null;
  organization_name: string;
  is_source_specific: boolean;
  is_provider_based?: boolean;
  is_signing_domain_based?: boolean;
  signing_domains_filter?: string[];
  signing_domain_context?: {
    signing_domains: string[];
    domain_name: string;
    is_aligned: boolean;
    dkim_pass_rate: number;
    total_messages: number;
    source_count: number;
    action_needed: string;
    recommendations: string[];
  };
  custom_message: string | null;
  generated_at?: string;
  provider_groups?: Array<{
    provider_id: string | null;
    provider_name: string;
    ip_count: number;
    total_volume: number;
    weighted_spf_rate: number;
    weighted_dkim_rate: number;
    weighted_spf_auth_rate: number;
    weighted_dkim_auth_rate: number;
    effective_status: string;
    source_ids: string[];
  }>;
  provider_info?: {
    id: string;
    name: string;
    category: string;
    sub_category: string | null;
    logo_url: string | null;
    documentation_url: string | null;
    setup_instructions: Record<string, unknown>;
    dkim_selectors: string[];
    spf_include: string | null;
    dkim_complexity: string | null;
    remediation_template: Record<string, unknown>;
    description: string | null;
  } | null;
  dkim_analysis?: {
    selectors: Array<{
      selector: string;
      signing_domain: string | null;
      pass: number;
      fail: number;
      total: number;
      pass_rate: number;
      aligned: boolean;
    }>;
    header_from_domains: string[];
    alignment_issue: boolean;
  } | null;
  disposition?: {
    none: number;
    quarantine: number;
    reject: number;
  };
  dmarc_record?: {
    policy: string;
    pct: number;
    sp: string | null;
    adkim: string | null;
    aspf: string | null;
  };
  enforcement?: {
    readiness_score: number;
    can_advance: boolean;
    next_policy: string | null;
    readiness_statement: string;
    blockers_count: number;
    spf_configured: boolean;
    dkim_configured: boolean;
  };
}

/**
 * APEX A1 (PR 45) — public Source-mirror payload. Returned by
 * GET /api/share/:token when the link was created with `sourceId`.
 * Read-only, no auth required. Mirrors the auth'd Source Detail
 * Overview surface: identity, 7-day summary, 30-day trend, plus a
 * single remediation hint. Comments, evidence-row detail, and DKIM
 * key hygiene are deliberately out of scope here (PR 46 introduces
 * per-recipient scoping that affects what's safe to surface).
 */
export interface PublicSourceMirrorResponse {
  kind: "source_mirror";
  domain: { name: string };
  organization_name: string;
  custom_message: string | null;
  branding: {
    logo_url: string | null;
    company_name: string | null;
    primary_color: string | null;
    accent_color: string | null;
  } | null;
  source: {
    id: string;
    displayName: string;
    classification: string;
    health: string;
    providerName: string | null;
    authIdentity: string | null;
    syntheticId: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
  };
  /**
   * APEX A3.1 (PR 48) — top-of-page status pill input. The "Failing
   * DMARC" red chip on the Overview header. Optional for backwards
   * compatibility with pre-PR-48 cached payloads.
   */
  headline?: {
    failingDmarc: boolean;
    statusLabel: string;
  };
  /**
   * APEX A3.1 (PR 48) — plain-English diagnosis sentence. Recipient-
   * facing copy: "Mail X sends as Y leaves Z signed as W — not Y —
   * so every message fails DMARC alignment."
   */
  diagnosis?: {
    sentence: string;
  };
  /**
   * APEX A3.1 (PR 48) — five-step pipeline for the "Where the break
   * happens" diagram. `state` colours each step.
   */
  pipeline?: Array<{
    step: number;
    label: string;
    sublabel: string;
    state: "ok" | "break" | "risk";
    icon: "mail" | "send" | "world" | "alert" | "inbox";
  }>;
  /**
   * APEX A3.1 (PR 48) — root-cause vs symptom callout. Both fields
   * are null when the source is healthy (no break to call out).
   */
  rootCauseSymptom?: {
    rootCause: { stepLabel: string; explanation: string } | null;
    symptom: { stepLabel: string; explanation: string } | null;
  };
  /**
   * APEX A3.1 (PR 48) — per-mechanism alignment from the latest
   * observation. Drives the "Inside step 4" card with mechanism /
   * alignment status pills. Null when no observation available yet.
   */
  alignment?: {
    spf: {
      mechanismResult:
        | "pass"
        | "fail"
        | "softfail"
        | "neutral"
        | "none"
        | "permerror"
        | "temperror";
      aligned: boolean;
      envelopeDomain: string | null;
    } | null;
    dkim: {
      signatureValid: boolean;
      aligned: boolean;
      signedDomain: string | null;
    } | null;
    dmarc: {
      result: "pass" | "fail" | "none";
      headerFromDomain: string | null;
    };
  } | null;
  summary: {
    windowDays: number;
    totalMessages: number;
    dmarcPassCount: number;
    dmarcPassRate: number | null;
    /** APEX A3.1 (PR 48) — 90-day Overview tile data. */
    window90?: {
      totalMessages: number;
      dmarcPassRate: number | null;
    };
    /** Sole sending IP across the 90-day window, or null if multiple. */
    soleSendingIp?: string | null;
    /** Distinct reporters seen in the 90-day window (alphabetical). */
    reporters?: string[];
    /** Date of the most-recent observation we've seen. */
    dataThrough?: string | null;
  };
  trend: Array<{
    date: string;
    totalMessages: number;
    dmarcPassRate: number | null;
  }>;
  /**
   * APEX A3.2 (PR 49) — Evidence tab data. Identity fingerprint,
   * per-report rows, and the "why this stream is yours" cross-tenant
   * reasoning from PR 39 (reverse DNS) + PR 40 (SPF membership).
   */
  evidence?: {
    fingerprint: {
      totalMessages: number;
      sourceIp: string | null;
      asn: string | null;
      asnOrg: string | null;
      dkimDomain: string | null;
      dkimSelector: string | null;
      spfEnvelopeDomain: string | null;
      variance: {
        sourceIp: boolean;
        dkimDomain: boolean;
        spfEnvelopeDomain: boolean;
      };
    } | null;
    aggregateReports: Array<{
      id: string;
      reportDate: string;
      reporterOrg: string;
      sourceIp: string;
      dkimDomain: string | null;
      spfDomain: string | null;
      dmarcResult: "pass" | "fail" | "none";
      messageCount: number;
    }>;
    totalAggregateReports: number;
    reporters: string[];
    whyYours: {
      sourceIp: string;
      reverseDns: {
        hostname: string | null;
        forwardConfirmed: boolean;
        brandedOrgDomain: string | null;
      } | null;
      spfMembership: Array<{
        domain: string;
        weight: "literal" | "shared_pool";
        cidr: string;
      }>;
    } | null;
  };
  remediation: {
    kind: "guided" | "needs_classification" | "not_mine" | "unknown";
    message: string | null;
  };
  /**
   * APEX A3.3 (PR 50) — Remediation tab plan. Two-party handshake
   * with ownership-coded steps + current-step marker + DNS records
   * inline at the publish step. Null when the source is Not-Mine,
   * Unclassified, or the provider's plan isn't modelled.
   */
  remediationPlan?: PublicRemediationPlan | null;
  /**
   * APEX A3.4 (PR 51) — Comments tab thread. Real comments
   * (operator + recipient) interleaved with Verkh system events,
   * oldest-first.
   */
  commentsThread?: PublicCommentsThreadEntry[];
  /**
   * APEX A3.5 (PR 52) — History tab. 4-tile accountability clock +
   * colour-coded timeline including outstanding/pending future
   * items.
   */
  history?: PublicHistory;
  /**
   * APEX A2 (PR 46) — populated when this response was fetched via a
   * per-recipient token (`/share/r/<recipientToken>`). The bare-link
   * path leaves it undefined. Name renders as a personalised greeting;
   * counters are exposed for the recipient's own awareness.
   */
  recipient?: {
    name: string | null;
    first_opened_at: string | null;
    last_opened_at: string | null;
    open_count: number;
  };
  /**
   * APEX A3 (PR 47) — handshake state for this (source, recipient)
   * pair. Null when no acknowledgement has been requested yet; in
   * that case the public page just renders the bare source-mirror
   * with no ack widget.
   */
  handshake?: PublicHandshake | null;
  generated_at?: string;
}

/** Public-share response — discriminated union on `kind`. */
export type PublicApexDashboardResponse =
  | PublicApexLegacyDashboardResponse
  | PublicSourceMirrorResponse;

/**
 * APEX A2 (PR 46) — per-recipient scoped link row. Returned by the
 * management endpoints under `/api/apex-links/:id/recipients`.
 */
export interface ApexLinkRecipient {
  id: string;
  apex_link_id: string;
  recipient_token: string;
  recipient_name: string | null;
  recipient_email: string | null;
  notes?: string | null;
  status: "active" | "revoked";
  first_opened_at: string | null;
  last_opened_at: string | null;
  open_count: number;
  created_at: string;
  /** Convenience URL — server constructs `${APP_URL}/share/r/<token>`. */
  share_url?: string;
}

/**
 * APEX 46.5 — one entry returned by GET /api/sources/:id/share-links.
 * Active source-scoped APEX link with its recipients embedded. Drives
 * the "links you've already created for this source" hydration on the
 * Source-Detail Share affordance.
 */
export interface SourceShareLink {
  id: string;
  token: string;
  title: string | null;
  custom_message: string | null;
  status: "active" | "revoked" | "expired";
  expires_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
  share_url: string;
  recipients: ApexLinkRecipient[];
}

export interface SourceShareLinksResponse {
  sourceId: string;
  domainName: string;
  links: SourceShareLink[];
}

/**
 * APEX A3 (PR 47) — two-party remediation handshake. State machine:
 *   requested → recipient_ack → closed
 * `cancelled` is reachable from any non-terminal state. Re-POSTing
 * after a terminal state re-opens to `requested`.
 */
export type RemediationHandshakeStatus =
  | "requested"
  | "recipient_ack"
  | "closed"
  | "cancelled";

export interface RemediationHandshake {
  id: string;
  sourceId: string;
  recipientId: string;
  apexLinkId: string;
  status: RemediationHandshakeStatus;
  requestNote: string | null;
  recipientAckNote: string | null;
  operatorAckNote: string | null;
  requestedAt: string;
  recipientAckAt: string | null;
  closedAt: string | null;
  cancelledAt: string | null;
  requestedByName: string | null;
  operatorAckUserName: string | null;
  recipient: {
    id: string;
    token: string;
    name: string | null;
    email: string | null;
    status: "active" | "revoked";
  };
}

/**
 * APEX A3 (PR 47) — handshake block on the public source-mirror
 * payload returned by `/api/share/r/:recipientToken`. Null when no
 * handshake has been requested for this (source, recipient) pair.
 */
export interface PublicHandshake {
  id: string;
  status: RemediationHandshakeStatus;
  request_note: string | null;
  recipient_ack_note: string | null;
  operator_ack_note: string | null;
  requested_at: string;
  recipient_ack_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
}

/**
 * APEX A3.5 (PR 52) — History tab. Accountability clock tiles +
 * colour-coded timeline of remediation events including outstanding
 * + pending future items.
 */
export interface PublicHistory {
  tiles: {
    firstSeen: string | null;
    linkShared: string | null;
    linkOpened: string | null;
    status: {
      label: string;
      tone: "danger" | "warning" | "success" | "neutral";
    };
  };
  dataThrough: string | null;
  events: Array<{
    id: string;
    timestamp: string | null;
    owner: "verkh" | "operator" | "recipient";
    ownerLabel: string;
    title: string;
    description: string;
    state: "past" | "outstanding" | "pending";
    tone: "neutral" | "warning";
    waitingSince: string | null;
  }>;
}

/**
 * APEX A3.4 (PR 51) — one entry in the public Comments tab timeline.
 * Either a real comment (operator or recipient attribution) or a
 * Verkh system event (link created, link opened, handshake event).
 */
export type PublicCommentsThreadEntry =
  | {
      kind: "comment";
      id: string;
      timestamp: string;
      body: string;
      authorRole: "operator" | "recipient";
      authorName: string;
      authorOrgLabel: string;
      authorInitials: string;
    }
  | {
      kind: "system_event";
      id: string;
      timestamp: string;
      icon: "bolt" | "bell" | "eye";
      text: string;
      tone: "neutral" | "warning";
    };

/**
 * APEX A3.3 (PR 50) — Remediation tab plan. Ownership-coded multi-step
 * handshake rendered on the public share page.
 */
export interface PublicRemediationPlan {
  mode: "two_party_handshake" | "single_party_dns" | "not_applicable" | "unknown";
  heroSentence: string;
  options: Array<{
    id: string;
    label: string;
    summary: string;
    badge: string;
    stepCount: number;
    recommended: boolean;
    /** PR 65 — second-line caveat under `summary`. Renders italic
     *  + slate-500 to flag operational follow-ups (API key rotation,
     *  webhooks, templates) that the step count doesn't convey. */
    operationalNote: string | null;
  }>;
  legendNote: string | null;
  steps: Array<{
    step: number;
    owner: "sender" | "domain_owner" | "verkh";
    ownerLabel: string;
    ownerIcon: "cloud" | "building" | "bolt";
    title: string;
    subnote: string | null;
    status: "done" | "current" | "waiting";
    statusLabel: string;
    records: Array<{
      type: "CNAME" | "TXT" | "MX";
      host: string;
      value: string;
    }> | null;
  }>;
  currentStep: number | null;
  yourMoveOwner: string | null;
  footerNote: string;
}

export interface SpineSourceDetailSummary {
  /** Trailing window the aggregate covers (currently always 7). */
  windowDays: number;
  totalMessages: number;
  dmarcPassCount: number;
  /** null when totalMessages is 0 — render "Insufficient data" rather than 0%. */
  dmarcPassRate: number | null;
  /** Domain-wide total over the same window, for the funnel-slice computation. */
  domainTotalMessages: number;
  /** source.totalMessages / domain.totalMessages. null when domain total is 0. */
  funnelSliceFraction: number | null;
}

export interface SpineSourceDetailResponse {
  source: SpineSource;
  summary: SpineSourceDetailSummary;
}

/**
 * One observation row in the Evidence tab (Phase 1 PR 15 +
 * drill-down extension). Maps to a single dmarc_report_records row
 * joined with its parent report + match-record metadata + legacy
 * sending_sources for PTR.
 */
export interface SpineEvidenceObservation {
  id: string;
  /** RUA report's external id (from the reporter's XML). */
  reportId: string;
  reporterOrg: string;
  reportDate: string;
  sourceIp: string;
  headerFrom: string | null;
  envelopeFrom: string | null;
  envelopeTo: string | null;
  /** Messages from this IP under this report bucket. */
  count: number;
  disposition: string;
  dmarcResult: string;
  spfResult: string;
  dkimResult: string;
  /** Primary signature shorthand — first sig's d= / selector. Convenience
   *  for compact-row display; the full array is under `authentication.dkim`. */
  dkimDomain: string | null;
  dkimSelector: string | null;
  /** Which matcher rule attributed this observation to this Source. */
  matchRule: string;
  matchConfidence: string;
  /** Drill-down: full auth detail (every DKIM signature, all SPF results). */
  authentication: {
    dkim: SpineDkimAuthEntry[];
    spf: {
      domain: string | null;
      scope: string | null;
      results: SpineSpfAuthEntry[];
    };
  };
  /** Drill-down: PTR + ASN + org + geo for the source IP. */
  ipEnrichment: {
    ptrHostname: string | null;
    /**
     * PR 39. True when the cron's reverse-DNS resolver confirmed
     * that the PTR hostname forward-resolves back to this IP's A
     * record. Forward-confirmed PTRs are customer-configured intent
     * (the strong-signal discriminator from the redesign review's
     * Section 6.4); a PTR without forward confirmation could be a
     * stray reverse-zone entry. Always present (false when not
     * confirmed or not yet resolved).
     */
    ptrForwardConfirmed: boolean;
    /**
     * PR 39. Registered-domain "brand" from the PTR hostname (last
     * two labels — e.g. "o1.sgout.debtpaypro.com" → "debtpaypro.com").
     * Null when no PTR or the hostname has fewer than two labels.
     * The column the T3 cross-tenant work in PR 41/42 will join on.
     */
    brandedOrgDomain: string | null;
    asn: string | null;
    org: string | null;
    country: string | null;
    city: string | null;
    /**
     * PR 40 (T2). Cross-tenant SPF-IP membership: which domains'
     * SPF chains list this IP, and how strongly each one claims it.
     * Ordered by weight ascending (literal first), then domain. The
     * load-bearing distinction from the redesign review's Section
     * 6.4: a `literal` membership means the chain root is in the
     * same registered domain as the row's domain (high signal);
     * `shared_pool` means the IP is reached via a third-party
     * include and the same row exists for every other tenant that
     * includes the same third party (low signal, ambiguous). Empty
     * array when the cron hasn't yet populated any chain that
     * covers this IP.
     */
    spfMembership: Array<{
      domain: string;
      weight: "literal" | "shared_pool";
      /** include: / redirect= chain from the domain's SPF root down
       *  to the mechanism that authorises this IP. Empty when the
       *  IP sits at the domain's own SPF (depth 0). */
      viaIncludeChain: string[];
    }>;
  };
  /** Drill-down: receiver-applied policy overrides (forwarded, mailing_list, etc.). */
  policyOverrideReasons: SpinePolicyOverrideEntry[];
}

export interface SpineDkimAuthEntry {
  domain: string | null;
  selector: string | null;
  result: string | null;
  humanResult?: string | null;
}

export interface SpineSpfAuthEntry {
  domain: string | null;
  scope: string | null;
  result: string | null;
  humanResult?: string | null;
}

export interface SpinePolicyOverrideEntry {
  type: string | null;
  comment?: string | null;
}

export interface SpineSourceEvidenceResponse {
  sourceId: string;
  domainId: string;
  domainName: string;
  windowDays: number;
  totals: {
    observationCount: number;
    messageCount: number;
  };
  observations: SpineEvidenceObservation[];
  pagination: {
    limit: number;
    offset: number;
    returned: number;
    hasMore: boolean;
  };
}

/**
 * One daily point on the source-detail trend sparkline (Phase 1 PR 18).
 * Every day in the requested window appears exactly once; days
 * without observations carry `dmarcPassRate: null` so the chart can
 * render a gap instead of implying 100% failure.
 */
export interface SpineTrendPoint {
  /** ISO yyyy-mm-dd, UTC day boundary. */
  date: string;
  totalMessages: number;
  dmarcPassCount: number;
  dmarcFailCount: number;
  /** Null when totalMessages is 0. */
  dmarcPassRate: number | null;
}

export interface SpineSourceTrendResponse {
  sourceId: string;
  /** Window length the response covers (clamped server-side to [1,90]). */
  days: number;
  /** Sorted ascending by date — oldest → newest, left → right on the chart. */
  points: SpineTrendPoint[];
}

/** Per-source bucket assignment computed by GET /api/sources. */
export type SpineSourceBucket =
  | "needs_work"
  | "review"
  | "ready"
  // PR 60 — Not-Mine sources surface in a dedicated Threats section
  // (visible by default, separate from the dismissed-noise hidden
  // bucket). The matcher's `presumed-spoof` rule auto-classifies
  // hostile traffic into here; user-flagged Not-Mine also lands here.
  | "threats"
  | null;

/**
 * One row in the GET /api/sources response (Phase 1 PR 11). Slimmer than
 * SpineSourceDetail — only the fields the 3-bucket list actually renders.
 * Carries the bucket assignment + window aggregate for the at-risk sort.
 */
export interface SpineSourceListRow {
  id: string;
  domainId: string;
  domainName: string;
  domainMode: string; // 'rollout' | 'operate'
  knownProviderId: string | null;
  knownProviderName: string | null;
  knownProviderCategory: string | null;
  authIdentity: string | null;
  syntheticId: string | null;
  asn: string | null;
  cidrBucket: string | null;
  classification: SpineClassification;
  confidencePinned: boolean;
  ownerUserId: string | null;
  /** Hydrated owner triple (PR 20). Null when unassigned. */
  owner: SpineSourceOwner | null;
  dismissedAt: string | null;
  health: SpineHealth;
  healthDmarcPassRate: number | null;
  healthMessageCount: number | null;
  healthWindowDays: number | null;
  healthEvaluatedAt: string | null;
  /** SUM(total_messages) over windowDays. */
  messagesInWindow: number;
  /** SUM(dmarc_fail_count) over windowDays. The at-risk sort metric. */
  messagesFailingInWindow: number;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Matcher rule that produced this Source (PR 29). Null on rows that
   * pre-date the matcher rerun or that still lack a match record. */
  matchRule: SpineMatcherRuleId | null;
  /** Server-computed bucket assignment. null = filtered (dismissed / Not-Mine). */
  bucket: SpineSourceBucket;
  /** Differential-diagnosis recommendation (PR 17). */
  recommendation: SpineRecommendation;
  /**
   * PR 54 — third-party vendor identified by reverse-DNS PTR of the
   * source's sending IP. Set when forward-confirmed AND the branded
   * org differs from the ESP's own org (e.g. "debtpaypro.com" on a
   * SendGrid source). Null when no PTR resolved, the PTR isn't
   * forward-confirmed, or the PTR points at the ESP itself. Used to
   * surface "operated by X" on the source row + pre-fill the share
   * recipient form.
   */
  vendorBrandedDomain: string | null;
  /** Free tier: provider names + auth identities are nulled out. */
  _tierRestricted: boolean;
  /**
   * PR 67 — when set, this row is a composite of N member sources
   * sharing the same (provider/vendor, domain, classification,
   * bucket). The matcher's per-/24 grain over-fragments shared mail
   * providers (Mimecast operates multiple /24s for the same logical
   * forwarding stream); the API's compose pass collapses these into
   * one row at read time. Member ids let the UI fan out a bulk
   * classify or merge call to all members. Absent on non-composite
   * rows.
   */
  compositeOf?: string[];
  compositeCount?: number;
  /**
   * PR 72 — server-derived composite display label. Falls back through
   * knownProviderName → vendorBrandedDomain → authIdentity → ASN
   * label → cidrBucket → asn → "Composite (N IP groups)". Always set
   * on composite rows; undefined on non-composite rows.
   */
  compositeLabel?: string;
}

/**
 * PR 68 — composite source detail response shape. Returned by
 * GET /api/sources/composite/:id. The `composite` is the aggregated
 * row (same shape as a list-page composite); `members` carry the
 * underlying per-/24 sources with full row data so the detail page
 * can render their individual stats and link to their existing
 * detail pages.
 */
export interface SpineCompositeSourceDetailResponse {
  composite: SpineSourceListRow;
  members: SpineSourceListRow[];
}

export interface SpineDomainSummary {
  id: string;
  name: string;
  mode: string; // 'rollout' | 'operate'
}

/**
 * Open matcher_review_required action item attached to a domain (and
 * optionally a Source). Surfaces in the Review bucket alongside pinned
 * Sources. PR 14 moves this behind the unified action-items endpoint.
 */
export interface SpineMatcherReviewItem {
  id: string;
  domainId: string;
  sourceId: string | null;
  fingerprint: string;
  title: string;
  description: string;
  severity: string;
  createdAt: string;
  sourceIdentifier: string | null;
}

export interface SpineSourceListResponse {
  windowDays: number;
  domains: SpineDomainSummary[];
  sources: SpineSourceListRow[];
  matcherReviewItems: SpineMatcherReviewItem[];
  tier: string;
  permissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
    canApplyRemediation: boolean;
  };
}

// ---------------------------------------------------------------------------
// Sources Spine — dashboard summary types (Phase 1 PR 12 cutover).
// ---------------------------------------------------------------------------

/**
 * One slice of the dashboard funnel bar. Server-computed bucket of
 * (classification, health) weighted by message volume over the window.
 */
export interface DashboardFunnelSegment {
  key:
    | "approved_aligned"
    | "approved_misconfigured"
    | "approved_no_data"
    | "forwarder"
    | "unclassified"
    | "not_mine";
  /** Human-readable label the server provides; the page renders verbatim. */
  label: string;
  /** Sum of total_messages for sources in this bucket over windowDays. */
  count: number;
  /** count / totalMessages. 0 when totalMessages is 0. */
  share: number;
  /**
   * PR 33 (H4). Per-segment DMARC outcome breakdown. Every segment
   * groups messages by *source* state (classification, health), not
   * by per-message outcome — so e.g. the "Unclassified" segment
   * counts every message from an Unclassified source, whether that
   * specific message passed DMARC or not. These two fields tell the
   * UI how many of `count` actually authenticated, so the legend +
   * tooltip can say "150k msgs (148k pass, 2k fail)" instead of
   * leaving the user to infer from the segment label.
   */
  dmarcPassCount: number;
  dmarcFailCount: number;
}

/**
 * Per-Source row inside whatsStuck / currentlyBleeding. Lean projection
 * of SpineSourceListRow — the dashboard only renders provider name +
 * volume + the at-risk metric, so the API returns just those fields.
 */
export interface DashboardSourceRow {
  id: string;
  domainId: string;
  domainName: string;
  domainMode: string;
  knownProviderId: string | null;
  knownProviderName: string | null;
  knownProviderCategory: string | null;
  authIdentity: string | null;
  syntheticId: string | null;
  asn: string | null;
  cidrBucket: string | null;
  classification: SpineClassification;
  confidencePinned: boolean;
  health: SpineHealth;
  healthDmarcPassRate: number | null;
  messagesInWindow: number;
  messagesFailingInWindow: number;
  _tierRestricted: boolean;
}

/**
 * Placeholder for the dashboard's Action Items module. PR 14 wires the
 * unified action-items endpoint; until then the dashboard renders
 * count + top 5 directly from this leg.
 */
export interface DashboardActionItem {
  id: string;
  domainId: string | null;
  kind: string;
  category: string;
  severity: string;
  title: string;
  description: string;
  createdAt: string;
  internalActionLink: string | null;
}

export interface DashboardSummaryResponse {
  windowDays: number;
  /**
   * Funnel slices in display order; the page renders the whole bar
   * and lets the user click into a segment to deep-link the /sources
   * page filtered by classification + health.
   */
  funnel: {
    totalMessages: number;
    domains: Array<{ id: string; name: string }>;
    segments: DashboardFunnelSegment[];
  };
  /** Full domain list — drives the per-row mode label on stuck/bleeding sources. */
  domains: SpineDomainSummary[];
  domainModes: { rolloutCount: number; operateCount: number; total: number };
  /**
   * Rollout-mode card. `show=true` when ANY domain in the org is in
   * rollout (journey not complete). Empty `sources` + show=true =
   * "nothing currently blocking progress" — meaningful in itself.
   */
  whatsStuck: { show: boolean; sources: DashboardSourceRow[] };
  /**
   * Operate-mode card. `show=true` when ANY domain is in operate (active
   * rejections are happening). Empty `sources` = "no bleeding right now."
   */
  currentlyBleeding: { show: boolean; sources: DashboardSourceRow[] };
  actionItems: { total: number; items: DashboardActionItem[] };
  tier: string;
  permissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
    canApplyRemediation: boolean;
  };
}

// ---------------------------------------------------------------------------
// Sources Spine — enforcement blast-radius types (Phase 1 PR 13).
// ---------------------------------------------------------------------------

/**
 * Per-Source row in a blast-radius response. Lean projection — only the
 * fields the /enforcement page renders (classification, health, volume,
 * the failing count, `isForwarder` for the hide-forwarders toggle).
 */
export interface BlastRadiusSourceRow {
  id: string;
  domainId: string;
  knownProviderId: string | null;
  knownProviderName: string | null;
  knownProviderCategory: string | null;
  authIdentity: string | null;
  syntheticId: string | null;
  asn: string | null;
  cidrBucket: string | null;
  classification: SpineClassification;
  confidencePinned: boolean;
  health: SpineHealth;
  healthDmarcPassRate: number | null;
  messagesInWindow: number;
  messagesFailingInWindow: number;
  /** classification === 'Forwarder'. Drives the page's hide-forwarders toggle. */
  isForwarder: boolean;
  _tierRestricted: boolean;
}

export interface BlastRadiusResponse {
  domainId: string;
  domainName: string;
  mode: string; // 'rollout' | 'operate'
  observedPolicy: "none" | "quarantine" | "reject" | null;
  /** What policy the response is simulating against (?at). */
  targetPolicy: "quarantine" | "reject";
  windowDays: number;
  /** Total messages observed for this domain in the window. */
  totalMessages: number;
  /**
   * "If you publish p=targetPolicy today, what gets blocked?" Every
   * failing-DMARC message counts; the per-source list helps the user
   * see WHICH senders they'd be silencing. Identical at quarantine vs.
   * reject — the framing differs, the number doesn't.
   */
  blastRadius: {
    wouldBeBlocked: number;
    wouldBeBlockedPct: number;
    sources: BlastRadiusSourceRow[];
  };
  /**
   * Operate-mode only: Approved-Misconfigured sources whose mail is
   * actively being rejected today. `show=true` when domain.mode is
   * 'operate' (under p=reject, failing-DMARC = rejected = bleeding).
   */
  currentlyBleeding: {
    show: boolean;
    messages: number;
    sources: BlastRadiusSourceRow[];
  };
  tier: string;
  permissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
  };
}

// Enforcement Readiness Types
export interface BlockingSource {
  id: string;
  identifier: string;
  name: string | null;
  providerName: string | null;
  /**
   * Auth identity (DKIM d= or SPF MAIL FROM org-domain) for
   * known-provider sources; null on synthetic-fallback rows. Use to
   * render the spine secondary identifier when present (PR 30).
   */
  authIdentity: string | null;
  /**
   * CIDR /24 bucket for synthetic-fallback sources; null when a known
   * provider matched. The Overview's "Sources Blocking Advancement"
   * list used to fall through to this string as the source's primary
   * identifier — PR 30 (B2) flipped the preference order so provider
   * name + auth identity win; this stays on the shape only for the
   * synthetic-only case where neither is available.
   */
  cidrBucket: string | null;
  /**
   * B-e. Short ASN-derived context line for sources whose identifier
   * fell through to the CIDR or synthetic fallback (e.g. "Amazon AWS
   * · unidentified operator"). Null when the identifier is already
   * meaningful or when no ASN-based enrichment is available. UI
   * renders in a small muted line beneath `identifier` only when
   * populated.
   */
  enrichmentLabel: string | null;
  trustScore: number | null;
  authorizationStatus: string;
  spfPassRate: number;
  dkimPassRate: number;
  totalMessages: number;
  issue: string;
  fix: string;
}

export interface EnforcementBlocker {
  type: "spf" | "dkim" | "pass_rate" | "sources" | "forwarding";
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  description: string;
  affectedMessages: number;
  remediation: {
    action: string;
    link?: string;
  };
}

export interface EnforcementRecommendation {
  type: "upgrade_policy" | "fix_source" | "review_source" | "configure_auth";
  title: string;
  description: string;
  link: string;
  priority: number;
  domainName?: string;
}

/**
 * One row of the enforcement-readiness rubric. The full rubric will
 * eventually land 11 of these; the B-d1 release wires the first four.
 * Cutover UI (B0) renders these as "X of N passing" with each row
 * clickable to its evidence link.
 */
/**
 * What kind of signal each check represents.
 *   "blocking"      — must be fixed before advancing. Drives the
 *                     "N blockers must be resolved first" copy on
 *                     the advance button.
 *   "advisory"      — should be true but resolves with time (policy
 *                     dwell) or future-proofs (SPF headroom). A
 *                     failing advisory does not block the advance.
 *   "informational" — confirmation of preparedness; not a gate.
 */
export type ReadinessCheckTier = "blocking" | "advisory" | "informational";

export interface ReadinessCheckResult {
  /** Stable machine identifier — used as a row key and for analytics. */
  id: string;
  /** Human-readable name shown on the rubric row. */
  name: string;
  /** Whether the gate is satisfied. not_applicable rows are not counted toward "X of N". */
  status: "pass" | "fail" | "not_applicable";
  /**
   * Whether a failing instance of this check actually blocks the
   * advance button or just sits as a note.
   */
  tier: ReadinessCheckTier;
  /** Short human-readable current state (e.g. "132 days", "97.2%"). */
  currentValue: string;
  /** Short human-readable gate (e.g. "≥30 days", "≥95%"). */
  threshold: string;
  /** One-sentence explanation for fail / not_applicable rows; null when passing. */
  reason: string | null;
  /** Optional in-app path to the evidence backing this check. */
  evidenceLink: string | null;
}

/**
 * A staged-rollout step on the DMARC ladder
 * (none -> q10 -> q25 -> q50 -> q100 -> r25 -> r50 -> r100). Mirrors
 * the NextStep type produced server-side by enforcement-helpers.
 */
export interface PolicyLadderStep {
  p: "none" | "quarantine" | "reject";
  pct: number;
  sp: "none" | "quarantine" | "reject";
  isPctRamp: boolean;
  /** e.g. "p=quarantine pct=10", "p=reject pct=100". */
  label: string;
}

export interface DomainReadiness {
  id: string;
  domainName: string;
  currentPolicy: "none" | "quarantine" | "reject" | null;
  /**
   * ISO timestamp of when the currently-observed published DMARC
   * policy first became stable on this domain. Drives the
   * "since YYYY-MM-DD · N days" line under the policy badge on the
   * Enforcement Readiness tile. Null until the mode-job has observed
   * a policy on the domain (typically a domain added in the last
   * few hours).
   */
  currentPolicySetAt: string | null;
  /**
   * pct= tag value parsed from the published DMARC record (0..100).
   * Defaults to 100 when the tag is absent. Used to pin the current
   * position on the policy-ramp stepper.
   */
  currentPolicyPct: number;
  /**
   * The single next staged step, or null when fully enforced. Drives
   * the advance button label ("Advance to p=quarantine pct=10").
   */
  proposedNextStep: PolicyLadderStep | null;
  /**
   * Full ladder from current position to p=reject pct=100. Empty when
   * fully enforced. Drives the stepper visualisation.
   */
  policyLadder: PolicyLadderStep[];
  /**
   * B-d1. Per-check rubric results for this domain. The B-d1 release
   * populates four entries (policy dwell, classified volume, open
   * investigations, DKIM coverage); future PRs append more without
   * changing existing entries' contracts. Ordering is stable across
   * deploys.
   */
  checks: ReadinessCheckResult[];
  targetPolicy: "none" | "quarantine" | "reject";
  riskSimulation: {
    currentDisposition: { pass: number; quarantine: number; reject: number };
    /**
     * Messages that fail DMARC in the window (PR 35, M4 fix). One
     * count for both stricter policies — quarantine and reject act
     * on the same set of failing messages; only the receiver's
     * disposition differs (quarantine = routed to spam, reject =
     * bounced). Render the count once and split the disposition
     * fork in the UI label, not in two duplicate-count boxes.
     */
    dmarcFailures: {
      count: number;
      percentage: number;
      /**
       * What kind of failures the count is made of (B3). Buckets are
       * mutually exclusive and sum to `count`. Renders as the
       * breakdown panel beneath the headline failing-messages
       * number on the Enforcement Readiness tile.
       */
      breakdown: {
        /** Real business mail; if you advance, lands in spam / bounces. */
        approved: number;
        /** Sources you have not triaged yet; could be real, could be spoofing. */
        unclassified: number;
        /** Explicitly marked as not yours; advancing the policy wins against these. */
        notMine: number;
        /** Forwarded mail; ARC signing on the original side would rescue. */
        forwarder: number;
        /** Observations without a Source row yet (matcher rerun still pending). */
        unmatched: number;
      };
    };
    totalMessages: number;
    timeframeDays: number;
  };
  authentication: {
    spfConfigured: boolean;
    spfPassRate: number;
    dkimConfigured: boolean;
    dkimPassRate: number;
    dmarcPassRate: number;
  };
  sources: {
    total: number;
    /** @deprecated PR 30 — read `byBucket.ready + byBucket.needsWork` (the Approved set). */
    authorized: number;
    /** @deprecated PR 30 — read `byBucket.review`. */
    pendingReview: number;
    /** @deprecated PR 30 — Not-Mine sources fall outside all three buckets. */
    unauthorized: number;
    /**
     * Triage buckets matching the /sources list vocabulary (PR 30, M1).
     *   needsWork: Approved+Misconfigured + Unclassified-non-pinned
     *   review:    pinned (any classification)
     *   ready:     Approved+Aligned + Approved+InsufficientData + Forwarder
     * Render these on the Domain Overview instead of the legacy
     * Authorized/Pending/Unauthorized trio.
     */
    byBucket: {
      needsWork: number;
      review: number;
      ready: number;
      /** Not-Mine. Added 2026-09-26 (finding 34) — the /sources list has
       *  had this bucket since PR 60 and the summary did not, so those
       *  Sources were counted in no bucket. The four still do not sum to
       *  `total`: dismissed Sources belong to none by design. */
      threats: number;
    };
    blockingSources: BlockingSource[];
  };
  blockers: EnforcementBlocker[];
  /**
   * Snapshot time for every figure on this response (PR 30, H1 fix).
   * The trailing window for source / message aggregates is 30 days
   * ending at this instant. Render alongside any figure so the user
   * can tell ordinary refresh drift between Dashboard and Overview
   * from a real computation split.
   */
  windowEndsAt: string;
  /**
   * @deprecated Nothing renders this. Read `checks[]` instead.
   *
   * The 0-100 composite the B0 cutover replaced with the rubric. It is
   * still computed and returned, and the reason recorded at the time —
   * "existing callers like the org-wide summary still read it" — does not
   * hold: a repo-wide grep finds no reader. `share.ts` computes its own
   * score by calling `calculateReadinessScore` directly, which is a second
   * instance of the same formula rather than a consumer of this field.
   *
   * Kept rather than deleted because it is a documented field of a public
   * response and an external SDK may read it. Removing it is a contract
   * break, which is a decision for whoever owns that contract — not
   * something to do while tidying.
   *
   * Worth knowing before relying on it: the response now carries three
   * overlapping verdicts on the same question — this score, `canAdvance`
   * plus `blockers[]`, and `checks[]` — computed from overlapping but
   * non-identical inputs, and they can disagree.
   */
  readinessScore: number;
  canAdvance: boolean;
  nextStep: string;
  readinessStatement: string; // Plain English explanation of readiness status
  recommendedAction: EnforcementRecommendation | null;
}

export interface EnforcementReadinessResponse {
  domains: DomainReadiness[];
  summary: {
    totalDomains: number;
    atReject: number;
    atQuarantine: number;
    atNone: number;
    readyToAdvance: number;
    overallProgress: number;
  };
  recommendations: EnforcementRecommendation[];
  permissions: {
    canViewSources: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
  };
}

export interface LabelSourceResponse {
  success: boolean;
  label: { category: string; subtype: string };
  // Async task generation (new pattern)
  jobId?: string;
  tasksGenerating?: boolean;
  // Sync task generation (legacy, may be removed)
  tasksGenerated?: number;
  tasks?: Array<{
    id: string;
    title: string;
    effort: string;
    risk: string;
    canAutoApply: boolean;
    apexRecommended: boolean;
  }>;
  permissions: {
    canApplyRemediation: boolean;
    canGenerateApex: boolean;
  };
}

export interface BulkLabelResponse {
  success: boolean;
  labeledCount: number;
  skippedCount: number;
  category: string;
  subtype: string;
  authorizationStatus: string;
}

export interface AutoLabelResult {
  sourceId: string;
  identifier: string;
  sourceName: string | null;
  providerName: string | null;
  previousStatus: string | null;
  newStatus: string;
  newCategory: string;
  newSubtype: string;
  reason: string;
  confidence: "high" | "medium" | "low";
}

export interface AutoLabelResponse {
  success: boolean;
  summary: {
    totalAnalyzed: number;
    sourcesNeedingLabels: number;
    autoAuthorized: number;
    flaggedSuspicious: number;
    needsReview: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
    dryRun: boolean;
    updatedCount: number;
  };
  results: AutoLabelResult[];
  message: string;
}

export interface AutoLabelPreview {
  totalSources: number;
  unlabeledSources: number;
  withKnownProvider: number;
  detectedForwarders: number;
  estimatedAutoLabeled: number;
  hint: string;
}

export interface TaskGenerationStatusResponse {
  generating: boolean;
  statusMessage: string | null;
  pendingJobs: Array<{
    id: string;
    sourceId: string;
    sourceName: string;
    labelCategory: string;
    status: string;
    createdAt: string;
  }>;
  completedJobs: Array<{
    id: string;
    sourceId: string;
    sourceName: string;
    labelCategory: string;
    status: string;
    tasksCreated: number;
    tasksSkipped: number;
    aiReasoning: string;
    skippedReasons: Array<{ taskType: string; reason: string }>;
    completedAt: string;
  }>;
}

export interface SourceDailyStat {
  date: string;
  message_count: number;
  // Alignment pass/fail (DMARC alignment results)
  spf_pass_count: number;
  spf_fail_count: number;
  dkim_pass_count: number;
  dkim_fail_count: number;
  // Auth pass (raw SPF/DKIM authentication, optional - may not be present in older data)
  spf_auth_pass_count?: number;
  dkim_auth_pass_count?: number;
  dmarc_pass_count: number;
  dmarc_fail_count: number;
}

export interface DkimSelectorStat {
  selector: string;
  pass: number;
  fail: number;
  total: number;
  passRate: number;
}

export interface DailyDetailSelectorStat {
  selector: string;
  dkim_domain?: string | null;
  pass: number;
  fail: number;
  total: number;
}

export interface DailyDetail {
  date: string;
  total_messages: number;
  spf_pass: number;
  spf_fail: number;
  dmarc_pass?: number;
  dmarc_fail?: number;
  disposition_none?: number;
  disposition_quarantine?: number;
  disposition_reject?: number;
  envelope_from?: string[];
  header_from?: string[];
  selectors: DailyDetailSelectorStat[];
}

export interface SourceMetadata {
  dkim_selectors: string[];
  dkim_selector_stats?: DkimSelectorStat[];
  envelope_from_domains: string[];
  daily_detail?: DailyDetail[];
}

export interface SourceStatsBatchResponse {
  stats: Record<string, SourceDailyStat[]>;
  metadata?: Record<string, SourceMetadata>;
}

export interface RemediationSummary {
  totalIssues: number;
  criticalIssues: number;
  highIssues: number | null;
  mediumIssues: number | null;
  lowIssues: number | null;
  lastUpdated?: string;
  tier: string;
  upgradeMessage?: string;
}

export interface RemediationTask {
  id: string;
  domain_id: string;
  source_id?: string;
  task_type: string;
  title: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  status: "pending" | "in_progress" | "completed" | "skipped" | "blocked";
  category?: string;
  type?: string;
  suggested_record?: string;
  domain_name: string;
  due_date?: string;
  created_at: string;
  // Automation capabilities
  can_auto_apply?: boolean;
  requires_vendor_action?: boolean;
  apex_recommended?: boolean;
  // Provider info
  provider_id?: string;
  provider_guide_url?: string;
  // Insight pipeline link
  insight_id?: string;
  // Recurrence tracking
  recurrence_count?: number;
  previous_task_id?: string;
}

export interface DkimSelector {
  id: string;
  selector: string;
  discovered_from: "discovered" | "manual";
  is_valid: boolean;
  key_type?: string;
  provider_hint?: string;
  last_checked_at?: string;
  validation_errors?: string;
}

// DKIM Selector Usage with source mapping
export interface DkimSelectorSource {
  sourceId: string | null;
  sourceName: string | null;
  sourceIdentifier: string | null;
  authorizationStatus: string | null;
  messageCount: number;
  passRate: number;
}

export interface DkimSelectorUsage {
  id: string | null;
  selector: string;
  providerHint: string | null;
  isValid: boolean | null;
  validationErrors: string[] | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  lastCheckedAt: string | null;
  usage: {
    totalMessages: number;
    passCount: number;
    failCount: number;
    passRate: number;
    periodStart: string | null;
    periodEnd: string | null;
  };
  sources: DkimSelectorSource[];
  totalSources: number;
  _untracked?: boolean;
}

export interface DkimSelectorUsageResponse {
  domainName: string;
  selectors: DkimSelectorUsage[];
  summary: {
    totalSelectors: number;
    validSelectors: number;
    invalidSelectors: number;
    untrackedSelectors: number;
  };
  tier: string;
  tierRestrictions: {
    canViewSourceDetails: boolean;
    canLabelSources: boolean;
  };
}

export interface SigningDomainProvider {
  provider_id: string | null;
  provider_name: string;
}

export interface SigningDomain {
  dkim_domain: string;
  aligned: boolean;
  is_forwarder: boolean;
  total_messages: number;
  dkim_pass: number;
  dkim_fail: number;
  pass_rate: number;
  ip_count: number;
  selectors: string[];
  providers: SigningDomainProvider[];
}

export interface SigningDomainsResponse {
  signing_domains: SigningDomain[];
  domain_name: string;
  misaligned_count: number;
  total_signing_domains: number;
}

export interface Milestone {
  type: string;
  label: string;
  order: number;
  achieved: boolean;
  achieved_at?: string;
}

export interface ApexLink {
  id: string;
  domain_id: string;
  domain_name?: string;
  token: string;
  title?: string;
  source_ids?: string[];
  /** APEX A1 (PR 45) — per-Source scope. Null/absent = legacy domain dashboard. */
  source_id?: string | null;
  /**
   * PR 71 — identifier fields hydrated server-side when `source_id`
   * is set. The share-links page resolves a human-readable label
   * from these (same fallback chain as the Sources list). All
   * nullable: a link to a sparse source may have only auth_identity
   * or cidr_bucket.
   */
  source_known_provider_name?: string | null;
  source_vendor_branded_domain?: string | null;
  source_auth_identity?: string | null;
  source_asn?: string | null;
  source_cidr_bucket?: string | null;
  custom_message?: string;
  expires_at?: string;
  view_count: number;
  last_viewed_at?: string;
  status: "active" | "expired" | "revoked";
  created_at: string;
  created_by_user_id?: string;
  created_by_name?: string;
}

export interface ApexLinkAnalytics {
  total_views: number;
  unique_visitors: number;
  views_by_day: Array<{ date: string; count: number }>;
  top_referrers: Array<{ referrer: string; count: number }>;
  avg_time_on_page?: number;
}

export interface DnsIntegration {
  id: string;
  provider_type: "cloudflare" | "godaddy";
  provider_name: string;
  domain_id: string | null;
  zone_id: string | null;
  zone_name: string | null;
  status: "pending" | "connected" | "error" | "expired" | "revoked";
  last_error: string | null;
  last_sync_at: string | null;
  last_health_check_at: string | null;
  created_at: string;
}

export interface DnsZone {
  id: string;
  name: string;
  status?: string;
}

export interface DnsRecord {
  id: string;
  type: "TXT" | "CNAME" | "MX" | "A" | "AAAA";
  name: string;
  content: string;
  ttl: number;
}

export interface DnsDetectionResult {
  provider: "cloudflare" | "godaddy" | null;
  confidence: "low" | "medium" | "high";
  nameservers: string[];
  unsupportedProvider?: string;
  canAutoConnect: boolean;
  cached: boolean;
  detectedAt?: string;
}

export interface DnsOperation {
  id: string;
  operation_type: "create" | "update" | "delete";
  record_type: string;
  record_name: string;
  record_value: string | null;
  previous_value: string | null;
  triggered_by: "user" | "remediation" | "auto_verify";
  status: "pending" | "success" | "failed";
  error_message: string | null;
  provider_type: string;
  provider_name: string;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
}

export interface IpWhitelistEntry {
  id: string;
  organization_id: string;
  ip_address: string;
  description: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  expires_at: string | null;
  is_active: number;
  last_used_at: string | null;
  use_count: number;
}

// SPF Optimization Types
export interface SpfResolveResult {
  record: string;
  lookup_count: number;
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  flattened_ipv4?: string[];
  flattened_ipv6?: string[];
  mechanism_tree?: unknown[];
}

export interface SpfChange {
  type: "added" | "removed" | "modified" | "consolidated" | "reordered" | "qualifier_change";
  mechanism: string;
  reason: string;
}

export interface SpfOptimizationResult {
  optimization_id: string;
  domain: string;
  current_spf: string | null;
  suggested_spf: string;
  diff: {
    before: string;
    after: string;
    changes: SpfChange[];
  };
  lookup_count: {
    before: number;
    after: number;
  };
  explanation: {
    summary: string;
    key_changes: string[];
    technical_notes?: string;
    risk_note?: string | null;
    recommendation?: string;
  };
  risk_level: "safe" | "caution" | "risky";
  validation: {
    is_valid: boolean;
    warnings: string[];
  };
  generated_at: string;
  can_apply: boolean;
}

// Error handling
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>,
    /**
     * The full parsed error body.
     *
     * `code`, `message` and `details` cover most responses, and everything
     * else the server sent used to be discarded here. That is how the IP
     * restriction guard lost `currentIp`: the 403 body carries it at the
     * top level — a documented field in `IpRestrictedError` — and there
     * was nowhere for it to land, so the guard displayed "Unknown".
     *
     * Keeping the body means a field added to an error response is
     * reachable without editing this class first.
     */
    public body?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// API Client
class ApiClient {
  private token: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private readonly REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh if <5 mins remaining

  // MSP client context - when set, requests include X-Client-Organization-Id header
  private clientOrgId: string | null = null;

  // Request cache for deduplication and short-term caching
  private requestCache = new Map<string, { data: unknown; timestamp: number; promise?: Promise<unknown> }>();
  private readonly CACHE_TTL_MS = 30 * 1000; // 30 seconds cache TTL

  /**
   * Set the active client organization for MSP context.
   * When set, all API requests will include the X-Client-Organization-Id header,
   * causing the API to return that client's data instead of the MSP's own data.
   */
  setClientOrg(clientOrgId: string | null) {
    if (this.clientOrgId !== clientOrgId) {
      this.clientOrgId = clientOrgId;
      // Clear cache when switching context to ensure fresh data
      this.clearCache();
    }
  }

  /**
   * Get the current client organization ID (if viewing as a client)
   */
  getClientOrg(): string | null {
    return this.clientOrgId;
  }

  /**
   * Clear the entire request cache
   */
  clearCache() {
    this.requestCache.clear();
  }

  /**
   * Clear a specific cache entry by key pattern
   */
  clearCacheByPattern(pattern: string) {
    for (const key of this.requestCache.keys()) {
      if (key.includes(pattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * Generate a context-aware cache key that includes the client org ID.
   * This ensures cache entries are scoped to the current organization context.
   */
  private cacheKey(key: string): string {
    return this.clientOrgId ? `${key}:client=${this.clientOrgId}` : key;
  }

  /**
   * Get a cached response if fresh, or execute the request
   * Also deduplicates in-flight requests to the same endpoint
   */
  private async cachedRequest<T>(
    cacheKey: string,
    fetcher: () => Promise<T>,
    options?: { ttl?: number; skipCache?: boolean }
  ): Promise<T> {
    const ttl = options?.ttl ?? this.CACHE_TTL_MS;

    // Skip cache if requested
    if (options?.skipCache) {
      return fetcher();
    }

    const cached = this.requestCache.get(cacheKey);
    const now = Date.now();

    // Return fresh cached data
    if (cached && now - cached.timestamp < ttl) {
      // If there's an in-flight request, wait for it
      if (cached.promise) {
        return cached.promise as Promise<T>;
      }
      return cached.data as T;
    }

    // Deduplicate in-flight requests
    if (cached?.promise) {
      return cached.promise as Promise<T>;
    }

    // Execute the request
    const promise = fetcher();

    // Store the promise for deduplication
    this.requestCache.set(cacheKey, {
      data: cached?.data,
      timestamp: cached?.timestamp ?? 0,
      promise,
    });

    try {
      const data = await promise;
      // Cache the result
      this.requestCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      return data;
    } catch (error) {
      // Remove failed request from cache
      this.requestCache.delete(cacheKey);
      throw error;
    }
  }

  /**
   * @deprecated Token storage in localStorage is being phased out.
   * Authentication now uses HTTP-only cookies. This method is kept
   * for backward compatibility during the migration period.
   *
   * After the migration, this will only clear the in-memory token
   * and remove any legacy localStorage entries.
   */
  setToken(token: string | null) {
    this.token = token;
    // Clean up any legacy localStorage entries (migration period)
    // We no longer write new tokens to localStorage for security
    if (!token) {
      localStorage.removeItem("auth_token");
    }
  }

  /**
   * @deprecated Token retrieval from localStorage is being phased out.
   * Authentication now uses HTTP-only cookies sent automatically with requests.
   * This method is kept for backward compatibility during the migration period.
   */
  getToken(): string | null {
    // During migration, check localStorage for legacy tokens
    // After migration is complete, this will only return in-memory token
    if (!this.token) {
      const legacyToken = localStorage.getItem("auth_token");
      if (legacyToken) {
        // Migrate: use the token but don't re-save it
        this.token = legacyToken;
        // Clear from localStorage for security
        localStorage.removeItem("auth_token");
      }
    }
    return this.token;
  }

  /**
   * Exchange an authorization code for a session.
   * The session is established via HTTP-only cookie (set by the server).
   * Returns user info for the auth store to use.
   */
  async exchangeCode(code: string): Promise<{
    success: boolean;
    isNewUser: boolean;
    user: { id: string; organization_id: string; role: string };
  }> {
    const response = await fetch(`${API_BASE}/auth/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
      credentials: "include", // Important: receive and store HTTP-only cookie
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.code || "EXCHANGE_FAILED",
        data.error || "Failed to complete authentication"
      );
    }

    // Clear any in-memory token since we're using cookies now
    this.token = null;

    return data;
  }

  /**
   * Check if the user has an active session via HTTP-only cookie.
   * This replaces the old token-based auth check.
   */
  async hasValidSession(): Promise<boolean> {
    try {
      await this.getMe();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Decode JWT payload without validation (validation happens server-side)
   */
  private decodeToken(token: string): { exp?: number; sub?: string } | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = parts[1];
      if (!payload) return null;
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired or about to expire
   */
  private isTokenExpiring(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload?.exp) return false;

    const expiresAt = payload.exp * 1000; // Convert to ms
    const now = Date.now();

    return expiresAt - now < this.REFRESH_THRESHOLD_MS;
  }

  /**
   * Refresh the token if it's about to expire
   * Uses a singleton promise to prevent concurrent refresh requests
   */
  private async ensureValidToken(): Promise<string | null> {
    const token = this.getToken();
    if (!token) return null;

    // Check if token needs refresh
    if (!this.isTokenExpiring(token)) {
      return token;
    }

    // If already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh
    this.refreshPromise = this.performTokenRefresh(token);

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(currentToken: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        credentials: "include", // Include HTTP-only cookies
      });

      if (!response.ok) {
        // If refresh fails with 401, token is invalid - clear it
        if (response.status === 401) {
          this.setToken(null);
          throw new ApiError(401, "SESSION_EXPIRED", "Your session has expired. Please log in again.");
        }
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      if (data.token) {
        this.setToken(data.token);
        return data.token;
      }

      throw new Error("No token in refresh response");
    } catch (error) {
      // If refresh fails, fall back to current token
      // It might still work for this request
      console.error("Token refresh failed:", error);
      return currentToken;
    }
  }

  /**
   * Handle demo mode by returning mock data instead of making API calls
   */
  private getMockData<T>(method: string, path: string, params?: Record<string, string | number>): T | null {
    // Auth endpoints
    if (path === "/auth/me") {
      return {
        user: {
          id: "user_demo",
          organization_id: "org_demo",
          role: "owner",
          email: "demo@acmecorp.com",
          name: "Demo User",
        },
      } as T;
    }

    // Organization
    if (path === "/api/organization") {
      return {
        organization: {
          id: "org_demo",
          name: mockOrganization.name,
          billing_tier: mockOrganization.billing_tier,
          tier: mockOrganization.tier,
          domain_count: mockOrganization.domains_used,
          created_at: new Date().toISOString(),
        },
      } as T;
    }

    // Domains
    if (path === "/api/domains" && method === "GET") {
      return {
        domains: mockDomains.map((d) => ({
          ...d,
          verification_token: "demo_token_" + d.id,
        })),
      } as T;
    }

    // Single domain
    const domainMatch = path.match(/^\/api\/domains\/([^/]+)$/);
    if (domainMatch && method === "GET") {
      const domain = mockDomains.find((d) => d.id === domainMatch[1]);
      if (domain) {
        return { domain: { ...domain, verification_token: "demo_token" } } as T;
      }
    }

    // Domain stats
    const domainStatsMatch = path.match(/^\/api\/domains\/([^/]+)\/stats$/);
    if (domainStatsMatch) {
      const domain = mockDomains.find((d) => d.id === domainStatsMatch[1]);
      return {
        stats: {
          total_reports: mockReports.filter((r) => r.domainId === domainStatsMatch[1]).length,
          total_messages: domain?.total_messages || 0,
          total_records: mockReports.filter((r) => r.domainId === domainStatsMatch[1]).reduce((sum, r) => sum + r.recordCount, 0),
        },
        breakdown: [
          { dmarc_result: "pass", message_count: Math.round((domain?.total_messages || 0) * 0.94) },
          { dmarc_result: "fail", message_count: Math.round((domain?.total_messages || 0) * 0.06) },
        ],
      } as T;
    }

    // Domain sources
    const domainSourcesMatch = path.match(/^\/api\/domains\/([^/]+)\/sources$/);
    if (domainSourcesMatch) {
      return {
        sources: mockSources.filter((s) => s.domain_id === domainSourcesMatch[1]),
      } as T;
    }

    // All sources
    if (path === "/api/sources" && method === "GET") {
      const permissions = mockTierPermissions[mockOrganization.tier];
      return {
        sources: mockSources,
        tier: mockOrganization.tier,
        permissions: {
          canViewHostname: permissions.canViewHostname,
          canLabelSources: permissions.canLabelSources,
          canViewRemediation: permissions.canViewRemediation,
          canApplyRemediation: permissions.canApplyRemediation,
        },
      } as T;
    }

    // Alerts
    if (path === "/api/alerts" && method === "GET") {
      const status = params?.status as string || "open";
      const filtered = status === "all"
        ? mockAlerts
        : mockAlerts.filter((a) => a.status === status || (status === "open" && a.status === "acknowledged"));
      return { alerts: filtered } as T;
    }

    // Reports
    if (path === "/api/reports" && method === "GET") {
      return { reports: mockReports } as T;
    }

    // Reports analytics
    if (path === "/api/reports/analytics" && method === "GET") {
      const days = Number(params?.days) || 30;
      return {
        timeSeries: mockAnalyticsData.dailyTrend.slice(-days).map((d) => ({
          date: d.date,
          totalMessages: d.totalMessages,
          dmarcPassRate: d.passRate,
          spfPassRate: d.spfPassRate,
          dkimPassRate: d.dkimPassRate,
          dispositionNone: Math.round(d.totalMessages * 0.94),
          dispositionQuarantine: Math.round(d.totalMessages * 0.05),
          dispositionReject: Math.round(d.totalMessages * 0.01),
        })),
        topReporters: [
          { reporter: "google.com", reportCount: 45, totalMessages: 45230 },
          { reporter: "microsoft.com", reportCount: 32, totalMessages: 28450 },
          { reporter: "yahoo.com", reportCount: 18, totalMessages: 12340 },
        ],
        summary: {
          totalMessages: mockDashboardStats.totalEmailsProcessed,
          dmarcPassRate: mockDashboardStats.passRate,
          spfPassRate: 95.2,
          dkimPassRate: 96.8,
          domainsWithData: mockDomains.filter((d) => d.total_messages > 0).length,
        },
        granularity: "daily" as const,
        dateRange: {
          start: mockAnalyticsData.dailyTrend[0]?.date || new Date().toISOString(),
          end: mockAnalyticsData.dailyTrend[mockAnalyticsData.dailyTrend.length - 1]?.date || new Date().toISOString(),
          days,
        },
      } as T;
    }

    // Reports by source
    if (path === "/api/reports/by-source" && method === "GET") {
      return {
        sources: mockSources.map((s) => ({
          sourceName: s.source_name,
          sourceIdentifier: s.identifier,
          authorizationStatus: s.authorization_status,
          reportCount: Math.floor(Math.random() * 20) + 5,
          totalMessages: s.total_message_count,
          spfPass: s.pass_count,
          spfFail: s.fail_count,
          dkimPass: Math.round(s.pass_count * 0.98),
          dkimFail: Math.round(s.fail_count * 1.02),
          dmarcPass: s.pass_count,
          dmarcFail: s.fail_count,
          dispositionNone: s.pass_count,
          dispositionQuarantine: Math.round(s.fail_count * 0.7),
          dispositionReject: Math.round(s.fail_count * 0.3),
          firstSeen: s.first_seen_at,
          lastSeen: s.last_seen_at,
        })),
      } as T;
    }

    // Remediation tasks
    if (path === "/api/remediation-tasks" && method === "GET") {
      const status = params?.status as string;
      const filtered = status ? mockRemediationTasks.filter((t) => t.status === status) : mockRemediationTasks;
      return {
        tasks: filtered.map((t) => ({
          ...t,
          can_auto_apply: t.can_auto_apply,
          requires_vendor_action: t.requires_vendor_action,
          apex_recommended: t.apex_recommended,
        })),
      } as T;
    }

    // Remediation summary
    if (path === "/api/sources/remediation-summary" && method === "GET") {
      return {
        totalIssues: mockRemediationStats.total,
        criticalIssues: mockRemediationStats.criticalCount,
        highIssues: mockRemediationStats.highCount,
        mediumIssues: 2,
        lowIssues: 1,
        tier: mockOrganization.tier,
      } as T;
    }

    // APEX links
    if (path === "/api/apex-links" && method === "GET") {
      return {
        apex_links: mockApexLinks.map((link) => ({
          id: link.id,
          domain_id: link.domain_id,
          domain_name: link.domain_name,
          token: link.token,
          title: link.title,
          expires_at: link.expires_at,
          view_count: link.view_count,
          status: link.status,
          created_at: link.created_at,
        })),
        limits: { used: mockApexLinks.filter((l) => l.status === "active").length, max: 3 },
      } as T;
    }

    // Invitations
    if (path === "/api/invitations" && method === "GET") {
      return {
        invitations: [
          {
            id: "inv_1",
            email: "john.doe@acmecorp.com",
            role: "member",
            status: "pending",
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            created_at: new Date().toISOString(),
          },
        ],
      } as T;
    }

    // User profile
    if (path === "/api/user/profile" && method === "GET") {
      return {
        profile: {
          id: "user_demo",
          name: "Demo User",
          email: "demo@acmecorp.com",
          role: "owner",
          created_at: new Date().toISOString(),
        },
      } as T;
    }

    // User preferences
    if (path === "/api/user/preferences" && method === "GET") {
      return {
        preferences: {
          weekly_digest: true,
          alert_emails: true,
          alert_severities: ["critical", "high", "medium", "low", "info"],
        },
      } as T;
    }

    // Events/Activity
    if (path === "/api/events" && method === "GET") {
      return {
        events: [
          { id: "evt_1", event_type: "domain.verified", entity_type: "domain", description: "Domain acmecorp.com was verified", created_at: new Date().toISOString() },
          { id: "evt_2", event_type: "alert.created", entity_type: "alert", description: "New critical alert: Unauthorized sender detected", created_at: new Date(Date.now() - 3600000).toISOString() },
          { id: "evt_3", event_type: "task.completed", entity_type: "task", description: "SPF record updated for marketing.acmecorp.com", created_at: new Date(Date.now() - 7200000).toISOString() },
        ],
        pagination: { total: 3, limit: 20, offset: 0, hasMore: false },
      } as T;
    }

    // Billing
    if (path === "/api/billing" && method === "GET") {
      return {
        billing: {
          current_tier: mockOrganization.tier,
          subscription: {
            id: "sub_demo",
            status: "active",
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            cancel_at_period_end: false,
          },
          payment_method: { brand: "visa", last4: "4242" },
          overage: null,
          invoices: [],
        },
      } as T;
    }

    // Source stats (for dashboard)
    if (path.includes("/source-stats")) {
      return mockSourceStats as T;
    }

    // Label options
    if (path === "/api/sources/labels/options" && method === "GET") {
      return {
        categories: ["authorized", "customer_support", "cicd", "marketing", "investigating", "malicious"],
        subtypes: {
          authorized: ["corporate", "transactional", "marketing"],
          customer_support: ["helpdesk", "ticketing", "live_chat"],
          cicd: ["infrastructure", "monitoring", "deployment"],
          marketing: ["newsletter", "campaigns", "drip"],
          investigating: ["unknown", "suspicious"],
          malicious: ["phishing", "spoofing", "spam"],
        },
      } as T;
    }

    // DNS integrations
    if (path === "/api/dns-integrations" && method === "GET") {
      return { integrations: [] } as T;
    }

    // API keys
    if (path === "/api/keys" && method === "GET") {
      return { keys: [] } as T;
    }

    // Default: return null to indicate no mock data available
    return null;
  }

  private async request<T>(
    method: string,
    path: string,
    options?: {
      body?: unknown;
      params?: Record<string, string | number>;
      skipTokenRefresh?: boolean;
    }
  ): Promise<T> {
    // In demo mode, return mock data instead of making API calls
    if (DEMO_MODE) {
      const mockData = this.getMockData<T>(method, path, options?.params);
      if (mockData !== null) {
        // Simulate network delay for realism
        await new Promise((resolve) => setTimeout(resolve, 100 + Math.random() * 200));
        return mockData;
      }
      // For unhandled endpoints, log a warning and return empty object
      console.warn(`[DEMO MODE] No mock data for ${method} ${path}`);
      return {} as T;
    }

    const url = new URL(`${API_BASE}${path}`, window.location.origin);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // Skip token refresh for auth endpoints to avoid infinite loops
    const token = options?.skipTokenRefresh
      ? this.getToken()
      : await this.ensureValidToken();

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // MSP client context - include header when viewing as a client
    if (this.clientOrgId) {
      headers["X-Client-Organization-Id"] = this.clientOrgId;
      console.log("[API] Sending request with client context:", this.clientOrgId, "to", url.pathname);
    }

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
      credentials: "include", // Include HTTP-only cookies for authentication
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.code || "UNKNOWN_ERROR",
        data.error || "An error occurred",
        data.details,
        data
      );
    }

    return data;
  }

  // Auth
  async getMe(): Promise<{ user: User }> {
    return this.request("GET", "/auth/me");
  }

  // Organization
  async getOrganization(options?: { skipCache?: boolean }): Promise<{ organization: Organization }> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/organization"),
      () => this.request("GET", "/api/organization"),
      options
    );
  }

  /**
   * Dashboard summary — Sources Spine cutover (Phase 1 PR 12).
   *
   * Replaces the legacy 5-widget compound endpoint with a spine-driven
   * shape: funnel (message-volume-weighted classification breakdown),
   * mode-conditional sections (whatsStuck for Rollout, currentlyBleeding
   * for Operate), and an action-items placeholder. The dashboard page
   * renders this top-to-bottom; no other widgets remain.
   */
  async getDashboardSummary(options?: {
    windowDays?: number;
  }): Promise<DashboardSummaryResponse> {
    const query =
      options?.windowDays !== undefined
        ? `?windowDays=${encodeURIComponent(String(options.windowDays))}`
        : "";
    return this.request("GET", `/api/dashboard/summary${query}`);
  }

  /**
   * Sources Spine — blast-radius simulator (Phase 1 PR 13).
   *
   * "If you publish p=<targetPolicy> on this domain today, what gets
   * blocked? And what's currently being rejected under your existing
   * policy?" — both questions in one response, framed by the domain's
   * mode (rollout vs operate).
   *
   * `?at` defaults to the next-step policy from the domain's observed
   * published policy. Explicit 'quarantine' / 'reject' overrides.
   *
   * `?windowDays` defaults to 30; clamped server-side to [1,90].
   */
  async getBlastRadius(
    domainId: string,
    options?: {
      at?: "quarantine" | "reject";
      windowDays?: number;
    }
  ): Promise<BlastRadiusResponse> {
    const params = new URLSearchParams();
    if (options?.at) params.set("at", options.at);
    if (options?.windowDays !== undefined) {
      params.set("windowDays", String(options.windowDays));
    }
    const query = params.toString();
    return this.request(
      "GET",
      `/api/enforcement/${domainId}/blast-radius${query ? `?${query}` : ""}`
    );
  }

  /**
   * @deprecated PR 13 introduced getBlastRadius for the /enforcement page.
   * This method stays alive temporarily for the domain-detail
   * EnforcementTab; PR 19 removes both.
   */
  async getEnforcementReadiness(): Promise<EnforcementReadinessResponse> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/enforcement/readiness"),
      () => this.request("GET", "/api/enforcement/readiness"),
      { ttl: 60000 } // 1 minute cache
    );
  }

  // Policy advancement - generate DNS record or verify DNS update
  async advanceDomainPolicy(
    domainId: string,
    targetPolicy: "quarantine" | "reject",
    verify?: boolean
  ): Promise<{
    record?: string;
    /**
     * B6. The currently-published DMARC record verbatim. Surfaced in
     * the advance modal so the caller can copy a back-out string
     * before they apply the advance — "if this breaks something, I
     * can paste this back in." Null when no record was published
     * pre-advance (the implicit-p=none case); the UI in that case
     * tells the caller to delete the DMARC TXT to revert.
     */
    rollbackRecord?: string | null;
    hostName?: string;
    verified?: boolean;
    currentPolicy?: string | null;
    message?: string;
  }> {
    return this.request("POST", `/api/domains/${domainId}/advance-policy`, {
      body: { targetPolicy, verify },
    });
  }

  async updateOrganization(data: Partial<Organization>): Promise<{ success: boolean }> {
    return this.request("PATCH", "/api/organization", { body: data });
  }

  async uploadOrganizationLogo(file: File): Promise<{ logo_url: string }> {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await fetch(`${API_BASE}/api/organization/logo`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(error.error || "Upload failed");
    }

    return response.json();
  }

  async deleteOrganizationLogo(): Promise<{ success: boolean }> {
    return this.request("DELETE", "/api/organization/logo");
  }

  // Domains
  async getDomains(options?: { skipCache?: boolean }): Promise<{ domains: Domain[] }> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/domains"),
      () => this.request("GET", "/api/domains"),
      options
    );
  }

  async createDomain(domain_name: string): Promise<{ domain: Domain }> {
    const result = await this.request<{ domain: Domain }>("POST", "/api/domains", { body: { domain_name } });
    this.clearCacheByPattern("/api/domains");
    return result;
  }

  async getDomain(id: string): Promise<{ domain: Domain }> {
    return this.request("GET", `/api/domains/${id}`);
  }

  async getDomainDetails(id: string): Promise<{
    domain: Domain;
    sources: Source[];
    dkimSelectors: DkimSelector[];
    tier: string;
    permissions: { canViewHostname: boolean; canLabelSources: boolean };
  }> {
    return this.request("GET", `/api/domains/${id}/details`);
  }

  async verifyDomain(id: string): Promise<{ verified: boolean; message?: string }> {
    return this.request("POST", `/api/domains/${id}/verify`);
  }

  async getDomainStats(
    id: string,
    days: number = 30
  ): Promise<{
    stats: { total_reports: number; total_messages: number; total_records: number };
    breakdown: Array<{ dmarc_result: string; message_count: number }>;
  }> {
    return this.request("GET", `/api/domains/${id}/stats`, { params: { days } });
  }

  async getDomainSources(id: string): Promise<{ sources: Source[] }> {
    return this.request("GET", `/api/domains/${id}/sources`);
  }

  /**
   * Get every Source the caller's org owns, shaped for the /sources
   * page's 3-bucket layout (Phase 1 PR 11 cutover).
   *
   * Each row carries the spine fields + a server-computed `bucket`
   * assignment + window aggregates for the at-risk sort. Window defaults
   * to 30 days (driven by the global TimeWindowSelector when the page
   * subscribes to the time-window store). Optional `domainId` scopes to
   * one domain — multi-domain orgs use the page's domain dropdown.
   *
   * Not cached: the response embeds the trailing-window aggregate so
   * cache invalidation would need to fire on every RUA batch. Page-level
   * state is cheap; let the page hold it.
   */
  async getAllSources(options?: {
    windowDays?: number;
    domainId?: string;
    /**
     * Owner filter (PR 20). Three modes:
     *   - omitted              → no constraint (every source)
     *   - 'unassigned'         → only sources with no owner
     *   - any user id          → only sources owned by that user
     */
    ownerUserId?: string | "unassigned";
  }): Promise<SpineSourceListResponse> {
    const params = new URLSearchParams();
    if (options?.windowDays !== undefined) {
      params.set("windowDays", String(options.windowDays));
    }
    if (options?.domainId) {
      params.set("domainId", options.domainId);
    }
    if (options?.ownerUserId) {
      params.set("ownerUserId", options.ownerUserId);
    }
    const query = params.toString();
    return this.request(
      "GET",
      `/api/sources${query ? `?${query}` : ""}`
    );
  }

  /**
   * Sources Spine — org user list for the Source-owner dropdown
   * (Phase 2 PR 20). Returns active org members ordered by role then
   * name. Used by `SourceOwnerSelect` to populate the assignee menu.
   *
   * Kept off the cache: org membership churns when invites are
   * accepted / users are removed, and the dropdown opens infrequently
   * enough that a per-open fetch is fine.
   */
  async getSourceOwners(): Promise<SpineOwnerListResponse> {
    return this.request("GET", "/api/sources/owners");
  }

  /**
   * Sources Spine — set / clear `sources.owner_user_id` (Phase 2 PR 20).
   *
   * Pass `null` to unassign. The endpoint validates that the target
   * user belongs to the caller's organisation; cross-org IDs 404 on
   * the user lookup with no existence leak on either side.
   */
  async setSourceOwner(
    sourceId: string,
    ownerUserId: string | null
  ): Promise<{ success: boolean; ownerUserId: string | null }> {
    return this.request("PATCH", `/api/sources/${sourceId}/owner`, {
      body: { ownerUserId },
    });
  }

  // -------------------------------------------------------------------------
  // Sources Spine — Comments + History (Phase 2 PR 21)
  //
  // Both surfaces sit on the Source detail page. Comments are the
  // "why did we trust this" discussion thread; History is the
  // append-only timeline of state changes the activity log records.
  // No caching: the comment + history endpoints already gate on the
  // source 404 so the round-trip is cheap, and tab switches need
  // fresh data.
  // -------------------------------------------------------------------------

  async getSourceComments(sourceId: string): Promise<SpineSourceCommentsResponse> {
    return this.request("GET", `/api/sources/${sourceId}/comments`);
  }

  async createSourceComment(
    sourceId: string,
    body: string
  ): Promise<{ comment: SpineSourceComment | null }> {
    return this.request("POST", `/api/sources/${sourceId}/comments`, {
      body: { body },
    });
  }

  async updateSourceComment(
    sourceId: string,
    commentId: string,
    body: string
  ): Promise<{ comment: SpineSourceComment | null }> {
    return this.request(
      "PATCH",
      `/api/sources/${sourceId}/comments/${commentId}`,
      { body: { body } }
    );
  }

  async deleteSourceComment(
    sourceId: string,
    commentId: string
  ): Promise<{ success: boolean }> {
    return this.request(
      "DELETE",
      `/api/sources/${sourceId}/comments/${commentId}`
    );
  }

  async getSourceHistory(
    sourceId: string,
    options?: { limit?: number }
  ): Promise<SpineSourceHistoryResponse> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    const query = params.toString();
    return this.request(
      "GET",
      `/api/sources/${sourceId}/history${query ? `?${query}` : ""}`
    );
  }

  /**
   * Sources Spine — per-Source guided remediation (Phase 3 PR 24).
   *
   * Returns the exact DNS records to publish to fix the source's
   * SPF + DKIM setup, composed from the known provider's
   * remediation_template + the domain's current DNS state. See
   * `SpineSourceRemediationResponse` for the three response shapes.
   */
  async getSourceRemediation(
    sourceId: string
  ): Promise<SpineSourceRemediationResponse> {
    return this.request("GET", `/api/sources/${sourceId}/remediation`);
  }

  /**
   * Pre-flight for the Publish button (Phase 3 PR 25). Returns the
   * connected DNS integration that would handle a publish for this
   * source's domain, or `null` if none. The Remediation tab uses
   * this to choose between the active publish CTA and a "Connect a
   * DNS provider" link.
   */
  async getSourceRemediationIntegration(
    sourceId: string
  ): Promise<SpineRemediationIntegrationResponse> {
    return this.request(
      "GET",
      `/api/sources/${sourceId}/remediation/integration`
    );
  }

  /**
   * Publish one or more remediation records via the connected DNS
   * provider (Phase 3 PR 25). Each record is published with
   * backup-before-write; the response carries per-record results so
   * the UI can render mixed success/failure cleanly.
   */
  async publishSourceRemediation(
    sourceId: string,
    body: {
      records: Array<{
        host: string;
        recordType: "TXT" | "CNAME";
        recommendedValue: string;
      }>;
    }
  ): Promise<SpineRemediationPublishResponse> {
    return this.request(
      "POST",
      `/api/sources/${sourceId}/remediation/publish`,
      { body }
    );
  }

  /**
   * Close-the-loop verification list (Phase 3 PR 26). Returns the
   * source's remediation_verifications history newest-first.
   * The Remediation tab polls this while a verification is pending
   * and renders a progress card that flips to "verified" once the
   * processor's health-job confirms Aligned.
   */
  async getSourceVerifications(
    sourceId: string
  ): Promise<SpineSourceVerificationsResponse> {
    return this.request("GET", `/api/sources/${sourceId}/verifications`);
  }

  /**
   * Sources Spine — per-Source detail endpoint (Phase 1 PR 8). Reads
   * from the new `sources` table populated by the matcher; the FIRST
   * production reader of the new spine. The legacy `getAllSources` /
   * `getSourcesForDomain` above still query `sending_sources` — those
   * cut over in PR 11.
   *
   * Returns the Source row + a trailing-window summary aggregate
   * (totalMessages, dmarcPass{Count,Rate}, funnel slice). Used by
   * the Source detail page's Overview tab.
   *
   * `windowDays` (Phase 1 PR 9): trailing window the summary
   * aggregate covers. Driven by the global TimeWindowSelector.
   * Defaults to 7 (matches PR 8's original behaviour) when omitted.
   */
  async getSourceDetail(
    sourceId: string,
    options?: { windowDays?: number }
  ): Promise<SpineSourceDetailResponse> {
    const query =
      options?.windowDays !== undefined
        ? `?windowDays=${encodeURIComponent(String(options.windowDays))}`
        : "";
    return this.request("GET", `/api/sources/${sourceId}/detail${query}`);
  }

  /**
   * PR 68 — composite source detail. Resolves a composite id
   * (`composite_<hash>` from the list endpoint's compose pass) to
   * the underlying member rows + the aggregate composite row.
   */
  async getCompositeSourceDetail(
    compositeId: string
  ): Promise<SpineCompositeSourceDetailResponse> {
    return this.request("GET", `/api/sources/composite/${compositeId}`);
  }

  /**
   * PR 69 — record a "don't compose" override for this composite's
   * group key. On the next list fetch the members will surface as
   * individual rows instead of one composite. Idempotent.
   */
  async splitComposite(compositeId: string): Promise<{ success: boolean }> {
    return this.request("POST", `/api/sources/composite/${compositeId}/split`);
  }

  /**
   * PR 69 — reverse a previously-recorded split. The composite
   * re-appears on the list on the next request. Idempotent.
   */
  async unsplitComposite(compositeId: string): Promise<{ success: boolean }> {
    return this.request(
      "DELETE",
      `/api/sources/composite/${compositeId}/split`
    );
  }

  /**
   * Sources Spine — per-Source evidence (Phase 1 PR 15).
   *
   * Raw RUA observations attached to this Source over the trailing
   * window. Drives the Evidence tab on the /sources/:id detail page,
   * replacing the dashboard-level Reports listing as the user's entry
   * point for inspecting raw DMARC report data.
   *
   * `windowDays` defaults to 30, clamped server-side to [1,90].
   * Pagination: `limit` defaults 100, max 500. `hasMore` in the
   * response indicates whether more rows exist beyond `offset+limit`.
   */
  async getSourceEvidence(
    sourceId: string,
    options?: { windowDays?: number; limit?: number; offset?: number }
  ): Promise<SpineSourceEvidenceResponse> {
    const params = new URLSearchParams();
    if (options?.windowDays !== undefined) {
      params.set("windowDays", String(options.windowDays));
    }
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) {
      params.set("offset", String(options.offset));
    }
    const query = params.toString();
    return this.request(
      "GET",
      `/api/sources/${sourceId}/evidence${query ? `?${query}` : ""}`
    );
  }

  /**
   * Sources Spine — full evidence export download.
   *
   * Fetches the whole evidence set for a Source in the current window
   * as either a CSV or JSON blob, then triggers a browser download.
   * Caps at 10,000 rows server-side; `truncated` surfaces via the
   * `X-Export-Truncated` response header (returned alongside the blob
   * so callers can warn the user).
   */
  async downloadSourceEvidenceExport(
    sourceId: string,
    options: { format: "csv" | "json"; windowDays?: number }
  ): Promise<{ blob: Blob; filename: string; truncated: boolean }> {
    const params = new URLSearchParams();
    params.set("format", options.format);
    if (options.windowDays !== undefined) {
      params.set("windowDays", String(options.windowDays));
    }

    const url = new URL(
      `${API_BASE}/api/sources/${sourceId}/evidence/export?${params.toString()}`,
      window.location.origin
    );

    const token = await this.ensureValidToken();
    const headers: HeadersInit = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (this.clientOrgId) headers["X-Client-Organization-Id"] = this.clientOrgId;

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      let message = "Export failed";
      try {
        const data = (await response.json()) as { error?: string; code?: string };
        message = data.error ?? message;
      } catch {
        // Non-JSON error body; keep the default.
      }
      throw new ApiError(response.status, "EXPORT_FAILED", message);
    }

    const blob = await response.blob();
    const truncated = response.headers.get("X-Export-Truncated") === "true";

    // Prefer the server-supplied filename when present (the API
    // builds a human-readable shape via buildEvidenceExportFilename
    // and exposes Content-Disposition via CORS). Fallback is
    // date-only — deliberately NOT the raw sourceId, which is an
    // internal identifier shape (`src_pa_synth_…`) we never want
    // surfacing in a customer-facing artifact.
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const fallbackDate = new Date().toISOString().split("T")[0];
    const filename =
      match?.[1] ?? `evidence-${fallbackDate}.${options.format}`;

    return { blob, filename, truncated };
  }

  /**
   * Sources Spine — per-Source DKIM key hygiene (Phase 3 PR 27).
   *
   * Returns one row per (signingDomain, selector) observed signing
   * mail for this Source, with status reflecting the most recent
   * daily DNS hygiene scan from the cron-worker. Status ordering:
   * revoked, weak, missing, surprise, then aligned.
   */
  async getSourceDkimKeys(
    sourceId: string
  ): Promise<SpineSourceDkimKeysResponse> {
    return this.request("GET", `/api/sources/${sourceId}/dkim-keys`);
  }

  /**
   * Sources Spine — ESP-subuser registry evidence (Tier-3 PR 41).
   *
   * Returns subuser identities extracted from each DKIM CNAME chain
   * attributed to this Source, plus AGGREGATE-ONLY cross-tenant
   * collision counts. Cross-tenant identifiers are never returned —
   * only the count of other orgs and a boolean for "first observed
   * here". Empty list for Sources whose provider has no registered
   * subuser extractor (most providers besides SendGrid today).
   */
  async getSourceSubuserEvidence(
    sourceId: string
  ): Promise<SpineSourceSubuserEvidenceResponse> {
    return this.request("GET", `/api/sources/${sourceId}/subuser-evidence`);
  }

  /**
   * Sources Spine — DKIM selector-collision evidence (Tier-3 PR 42).
   *
   * Returns per-(signingDomain, selector) collision facets: other
   * Sources in this org sharing the pair (identifiers returned),
   * cross-org observer count (aggregate), and the strong DNS-collision
   * signal — cross-org subuser-collision count (aggregate). Cross-org
   * identifiers are never exposed.
   */
  async getSourceDkimCollisions(
    sourceId: string
  ): Promise<SpineSourceDkimCollisionsResponse> {
    return this.request("GET", `/api/sources/${sourceId}/dkim-collisions`);
  }

  /**
   * Sources Spine — cross-source remediation leads (Tier-3 PR 43).
   *
   * Returns sibling Sources within the caller's org the user might
   * route this Source's mail through instead of fixing it in place —
   * either on the same domain (the primary lead) or sharing the same
   * known_provider on a different domain (a known-good mirror).
   * Tenant-scoped end-to-end; no cross-tenant data.
   */
  async getSourceRemediationLinkage(
    sourceId: string
  ): Promise<SpineSourceRemediationLinkageResponse> {
    return this.request("GET", `/api/sources/${sourceId}/remediation-linkage`);
  }

  /**
   * APEX 46.5 — list active source-scoped APEX links for this Source
   * with their recipients embedded. Used by the Source-Detail share
   * affordance to hydrate on mount so existing links persist across
   * page reloads.
   */
  async getSourceShareLinks(
    sourceId: string
  ): Promise<SourceShareLinksResponse> {
    return this.request("GET", `/api/sources/${sourceId}/share-links`);
  }

  /** APEX A3 (PR 47) — list every handshake for this Source. */
  async getSourceHandshakes(
    sourceId: string
  ): Promise<{ handshakes: RemediationHandshake[] }> {
    return this.request("GET", `/api/sources/${sourceId}/handshakes`);
  }

  /**
   * APEX A3 (PR 47) — operator initiates a handshake with an existing
   * recipient. Re-POSTing on a terminal-state handshake re-opens it
   * in place; POSTing on a non-terminal one returns 409.
   */
  async createSourceHandshake(
    sourceId: string,
    options: { recipientId: string; requestNote?: string }
  ): Promise<{ handshake: { id: string; status: RemediationHandshakeStatus } }> {
    return this.request("POST", `/api/sources/${sourceId}/handshakes`, {
      body: {
        recipientId: options.recipientId,
        requestNote: options.requestNote,
      },
    });
  }

  /**
   * APEX A3 (PR 47) — operator transitions a handshake to a terminal
   * state. `status` must be 'closed' or 'cancelled'; the recipient
   * controls 'recipient_ack' via the public endpoint.
   */
  async patchSourceHandshake(
    sourceId: string,
    handshakeId: string,
    options: { status: "closed" | "cancelled"; operatorAckNote?: string }
  ): Promise<{ handshake: { id: string; status: RemediationHandshakeStatus } }> {
    return this.request(
      "PATCH",
      `/api/sources/${sourceId}/handshakes/${handshakeId}`,
      {
        body: {
          status: options.status,
          operatorAckNote: options.operatorAckNote,
        },
      }
    );
  }

  /**
   * APEX A3 (PR 47) — public recipient acknowledgement. No auth other
   * than possession of the recipient token. Transitions
   * `requested` → `recipient_ack`. If already in `recipient_ack`,
   * updates the note in place. 409 on terminal states.
   */
  async acknowledgePublicHandshake(
    recipientToken: string,
    options?: { note?: string }
  ): Promise<{ handshake: { id: string; status: RemediationHandshakeStatus } }> {
    return this.request("POST", `/api/share/r/${recipientToken}/acknowledge`, {
      body: { note: options?.note },
    });
  }

  /**
   * APEX A3.4 (PR 51) — recipient posts a comment from the public
   * share page. Body is required, max 10 000 chars. 410 on revoked
   * or expired link.
   */
  async postPublicComment(
    recipientToken: string,
    body: string
  ): Promise<{ comment: { id: string; timestamp: string; body: string } }> {
    return this.request("POST", `/api/share/r/${recipientToken}/comments`, {
      body: { body },
    });
  }

  /**
   * Sources Spine — SPF lookup-budget meter (Phase 3 PR 28).
   *
   * Returns the domain's current resolved SPF DNS-lookup count
   * (recursive, from ai-worker), the RFC 7208 §4.6.4 cap of 10, and
   * per-vendor projections. Used by the meter card on the
   * Enforcement page and the pre-approve callout in the classify
   * flow on /sources.
   */
  async getSpfBudget(domainId: string): Promise<SpfBudgetResponse> {
    return this.request("GET", `/api/spf/${domainId}/budget`);
  }

  /**
   * Sources Spine — per-Source DMARC pass-rate trend (Phase 1 PR 18).
   *
   * Daily series from `source_daily_stats` for the sparkline on
   * the source detail page. Every day in the trailing window appears
   * exactly once — days without observations carry
   * `dmarcPassRate: null` so the chart renders a gap rather than
   * implying 100% failure.
   *
   * `days` clamps server-side to [1, 90], defaults to 90.
   */
  async getSourceTrend(
    sourceId: string,
    options?: { days?: number }
  ): Promise<SpineSourceTrendResponse> {
    const query =
      options?.days !== undefined
        ? `?days=${encodeURIComponent(String(options.days))}`
        : "";
    return this.request("GET", `/api/sources/${sourceId}/trend${query}`);
  }

  /**
   * Sources Spine — classify a single Source (Phase 1 PR 10).
   *
   * Writes `sources.classification` (the new spine field). Pass `null`
   * to revert to 'Unclassified'. Settled values stamp `owner_user_id`
   * to the caller so the merge-conflict invariant has a clear "decided
   * by" signal.
   *
   * Prior to PR 11 this also dual-wrote `sending_sources.authorization_status`
   * so the legacy /sources page stayed coherent; PR 11 retired the
   * legacy UI and the dual-write was removed.
   */
  async classifySource(
    sourceId: string,
    classification: SpineClassification | null
  ): Promise<{
    success: boolean;
    classification: SpineClassification;
  }> {
    return this.request("PATCH", `/api/sources/${sourceId}/classify`, {
      body: { classification },
    });
  }

  /**
   * Sources Spine — dismiss / un-dismiss a Source (Phase 1 PR 10).
   *
   * Sets / clears `sources.dismissed_at`. The /sources page (PR 11)
   * hides dismissed Sources from the Needs Work bucket. Orthogonal to
   * classification — a Source can be dismissed in any classification
   * state. No legacy dual-write: `sending_sources` has no dismissal
   * concept.
   *
   * `dismissed` defaults to true; pass false to un-dismiss.
   */
  async dismissSource(
    sourceId: string,
    dismissed: boolean = true
  ): Promise<{ success: boolean; dismissed: boolean }> {
    return this.request("PATCH", `/api/sources/${sourceId}/dismiss`, {
      body: { dismissed },
    });
  }

  /**
   * Sources Spine — bulk-classify many Sources (Phase 1 PR 10).
   *
   * Applies the same classification to every Source in `sourceIds`
   * that belongs to the caller's org. Cross-org ids drop silently;
   * `appliedCount < requestedCount` in the response means at least
   * one id was filtered out (no existence leak — same shape as a
   * fresh list page would show).
   */
  async bulkClassifySources(
    sourceIds: string[],
    classification: SpineClassification | null
  ): Promise<{
    success: boolean;
    classification: SpineClassification;
    requestedCount: number;
    appliedCount: number;
  }> {
    return this.request("POST", `/api/sources/bulk-classify`, {
      body: { source_ids: sourceIds, classification },
    });
  }

  // Get available label categories and subtypes
  async getLabelOptions(): Promise<{
    categories: readonly string[];
    subtypes: Record<string, readonly string[]>;
  }> {
    return this.request("GET", "/api/sources/labels/options");
  }

  // Label a source and generate remediation tasks
  async labelSource(
    sourceId: string,
    category: string,
    subtype: string,
    notes?: string
  ): Promise<LabelSourceResponse> {
    return this.request("POST", `/api/sources/${sourceId}/label`, {
      body: { category, subtype, notes },
    });
  }

  // Bulk label multiple sources (batches into chunks of 100 to match API limit)
  async bulkLabelSources(
    sourceIds: string[],
    category: string,
    subtype: string,
    notes?: string
  ): Promise<BulkLabelResponse> {
    const BATCH_SIZE = 100;

    if (sourceIds.length <= BATCH_SIZE) {
      return this.request("POST", "/api/sources/bulk-label", {
        body: { sourceIds, category, subtype, notes },
      });
    }

    // Split into chunks and run sequentially to avoid rate limiting
    let totalLabeled = 0;
    let totalSkipped = 0;
    let lastResult: BulkLabelResponse | null = null;

    for (let i = 0; i < sourceIds.length; i += BATCH_SIZE) {
      const chunk = sourceIds.slice(i, i + BATCH_SIZE);
      const result = await this.request<BulkLabelResponse>("POST", "/api/sources/bulk-label", {
        body: { sourceIds: chunk, category, subtype, notes },
      });
      totalLabeled += result.labeledCount;
      totalSkipped += result.skippedCount;
      lastResult = result;
    }

    return {
      success: true,
      labeledCount: totalLabeled,
      skippedCount: totalSkipped,
      category: lastResult!.category,
      subtype: lastResult!.subtype,
      authorizationStatus: lastResult!.authorizationStatus,
    };
  }

  // Authorize/label all sources for a known provider on a specific domain
  async authorizeProvider(
    providerId: string,
    domainId: string,
    category: string,
    subtype: string,
    notes?: string
  ): Promise<{ success: boolean; updatedCount: number; providerName: string; authorizationStatus: string }> {
    return this.request("POST", `/api/sources/provider/${encodeURIComponent(providerId)}/authorize`, {
      body: { category, subtype, domainId, notes },
    });
  }

  // Auto-label sources using AI-powered analysis
  async autoLabelSources(options?: {
    domainId?: string;
    dryRun?: boolean;
    onlyUnlabeled?: boolean;
  }): Promise<AutoLabelResponse> {
    return this.request("POST", "/api/sources/auto-label", {
      body: options || {},
    });
  }

  // Get auto-label preview (counts without making changes)
  async getAutoLabelPreview(domainId?: string): Promise<AutoLabelPreview> {
    const query = domainId ? `?domainId=${encodeURIComponent(domainId)}` : "";
    return this.request("GET", `/api/sources/auto-label/preview${query}`);
  }

  // Get remediation issue summary (free tier sees counts only)
  async getRemediationSummary(): Promise<RemediationSummary> {
    return this.request("GET", "/api/sources/remediation-summary");
  }

  // Get task generation status (for async task generation)
  async getTaskGenerationStatus(): Promise<TaskGenerationStatusResponse> {
    return this.request("GET", "/api/sources/task-generation-status");
  }

  // Get batch daily stats for multiple sources (for expandable source details)
  async getSourceStatsBatch(sourceIds: string[], days: number = 30): Promise<SourceStatsBatchResponse> {
    const ids = sourceIds.join(",");
    return this.request("GET", `/api/sources/stats/batch?ids=${encodeURIComponent(ids)}&days=${days}`);
  }

  // Backfill source daily stats from historical dmarc_report_records (Admin only)
  async backfillSourceStats(options?: {
    domainId?: string;
    sourceIds?: string[];
  }): Promise<{
    success: boolean;
    message: string;
    sourcesProcessed: number;
    datesProcessed: number;
  }> {
    return this.request("POST", "/api/admin/backfill-source-stats", {
      body: options || {},
    });
  }

  // Generate APEX link from remediation task
  async generateApexFromTask(taskId: string): Promise<{
    success: boolean;
    apexLink: {
      id: string;
      url: string;
      token: string;
      expiresAt: string | null;
      sourceName: string;
      taskId: string;
    };
    usage: {
      current: number;
      limit: number;
    };
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/generate-apex`);
  }

  // Revoke APEX link from remediation task
  async revokeApexFromTask(taskId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/remediation-tasks/${taskId}/revoke-apex`);
  }

  async getDomainMilestones(
    id: string
  ): Promise<{ milestones: Milestone[]; progress: Milestone[]; completion_percentage: number }> {
    return this.request("GET", `/api/domains/${id}/milestones`);
  }

  async getSuggestedSpf(id: string): Promise<{
    current_spf: string | null;
    suggested_spf: string;
    includes: string[];
    ip_addresses: string[];
    dns_lookup_estimate: number;
    warnings: string[];
  }> {
    return this.request("GET", `/api/domains/${id}/suggested-spf`);
  }

  async getSuggestedDmarc(id: string): Promise<{
    current_dmarc: string | null;
    current_policy: string | null;
    current_pct: number;
    pass_rate: number;
    policy_goal: string;
    recommendation: {
      action: string;
      suggested_record: string;
      reasoning: string;
      risk_level: "low" | "medium" | "high";
    };
  }> {
    return this.request("GET", `/api/domains/${id}/suggested-dmarc`);
  }

  async refreshDns(id: string): Promise<{
    dmarc: { raw: string | null; valid: boolean; errors: string[] };
    spf: { raw: string | null; valid: boolean; errors: string[] };
    mx: { raw: string | null; valid: boolean; errors: string[] };
    refreshed_at: string;
    next_refresh_available_at: string;
  }> {
    return this.request("POST", `/api/domains/${id}/refresh-dns`);
  }

  // DKIM
  async getDkimSelectors(domainId: string): Promise<{ selectors: DkimSelector[] }> {
    return this.request("GET", `/api/domains/${domainId}/dkim`);
  }

  async addDkimSelector(
    domainId: string,
    selector: string,
    provider_hint?: string
  ): Promise<{ selector: DkimSelector }> {
    return this.request("POST", `/api/domains/${domainId}/dkim`, {
      body: { selector, provider_hint },
    });
  }

  async validateDkimSelector(
    domainId: string,
    selectorId: string
  ): Promise<{ valid: boolean; error?: string; record?: string }> {
    return this.request("POST", `/api/domains/${domainId}/dkim/${selectorId}/validate`);
  }

  async deleteDkimSelector(domainId: string, selectorId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/domains/${domainId}/dkim/${selectorId}`);
  }

  // Get DKIM selector usage with source mapping
  async getDkimSelectorUsage(domainId: string): Promise<DkimSelectorUsageResponse> {
    return this.request("GET", `/api/domains/${domainId}/dkim/usage`);
  }

  // Signing Domains
  async getSigningDomains(domainId: string): Promise<SigningDomainsResponse> {
    return this.request("GET", `/api/sources/signing-domains?domainId=${domainId}`);
  }

  // BIMI (Brand Indicators for Message Identification)
  async getBimiReadiness(domainId: string): Promise<BimiReadinessResponse> {
    return this.request("GET", `/api/domains/${domainId}/bimi/readiness`);
  }

  async validateBimiLogo(
    domainId: string,
    logoUrl: string
  ): Promise<BimiLogoValidationResult> {
    return this.request("POST", `/api/domains/${domainId}/bimi/validate-logo`, {
      body: { logo_url: logoUrl },
    });
  }

  async generateBimiRecord(
    domainId: string,
    input: { logo_url: string; vmc_url?: string; selector?: string }
  ): Promise<BimiRecordGeneratorResponse> {
    return this.request("POST", `/api/domains/${domainId}/bimi/generate-record`, {
      body: input,
    });
  }

  // Sources
  async updateSource(
    id: string,
    data: { authorization_status?: string; provider_name?: string; notes?: string }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/sources/${id}`, { body: data });
  }

  async approveSource(id: string): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/sources/${id}`, { body: { authorization_status: "authorized" } });
  }

  // Recalculate pass rates from historical data
  async recalculatePassRates(domainId?: string): Promise<{
    success: boolean;
    message: string;
    rowsUpdated: number;
    domainId?: string;
  }> {
    if (domainId) {
      return this.request("POST", `/api/sources/recalculate-pass-rates/${domainId}`);
    }
    return this.request("POST", "/api/sources/recalculate-pass-rates");
  }

  // Backfill missing sending_sources from report records
  async backfillSources(): Promise<{
    success: boolean;
    message: string;
    found: number;
    created: number;
    errors: number;
  }> {
    return this.request("POST", "/api/sources/backfill");
  }

  // Alerts — the four methods that were here (getAlerts, updateAlert,
  // acknowledgeAlert, resolveAlert) are gone. They called `/api/alerts`,
  // which migration 0081 folded into `action_items`; the router at
  // routes/reports.ts now answers 410 Gone on every path under it. So these
  // could not succeed, and nothing called them — they were a working-looking
  // API for an endpoint that had been deliberately tombstoned.
  //
  // The replacement is the action-items surface, filtered: `?kind=alert`.

  async snoozeInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "snoozed" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  async resolveInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "resolved" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  async suppressInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "not_applicable" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    this.clearCacheByPattern("/api/ai/suppressions");
    return result;
  }

  async getInsightSuppressions(): Promise<{ suppressions: AIInsightSuppression[] }> {
    return this.request<{ suppressions: AIInsightSuppression[] }>("GET", `/api/ai/suppressions`);
  }

  async deleteInsightSuppression(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("DELETE", `/api/ai/suppressions/${id}`);
    this.clearCacheByPattern("/api/ai/suppressions");
    return result;
  }

  // Reports
  async getReports(params?: { limit?: number; offset?: number; domainId?: string; days?: number }): Promise<{
    reports: Array<{
      id: string;
      reportId: string;
      orgName: string;
      dateRangeBegin: string;
      dateRangeEnd: string;
      domainId: string;
      domainName?: string;
      recordCount: number;
      totalMessageCount: number;
      processedAt: string;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
    }>;
    summary: {
      totalMessages: number;
      dmarcCapableMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      forwarderMessages: number;
    };
  }> {
    return this.request("GET", "/api/reports", { params });
  }

  // Get raw records for a specific report (drill-down)
  async getReportRecords(reportId: string): Promise<{
    report: {
      id: string;
      reportId: string;
      domainName: string;
    };
    records: Array<{
      source_ip: string;
      count: number;
      disposition: string;
      spf_result: string;
      spf_domain: string | null;
      dkim_result: string;
      dkim_domain: string | null;
      dmarc_result: string;
      header_from: string | null;
      envelope_from: string | null;
      source_ip_org: string | null;
      source_ip_asn: string | null;
    }>;
    summary: {
      totalRecords: number;
      uniqueSpfResults: string[];
      uniqueDkimResults: string[];
      uniqueDmarcResults: string[];
    };
  }> {
    return this.request("GET", `/api/reports/${reportId}/records`);
  }

  // Get reports aggregated by sending source (origin)
  // Now supports pagination - defaults to 1000 (API max) to get more sources
  async getReportsBySource(params?: { limit?: number; offset?: number; days?: number }): Promise<{
    sources: Array<{
      sourceName: string;
      sourceIdentifier: string;
      authorizationStatus: "authorized" | "unauthorized" | "pending_review" | "ignored" | null;
      ptrHostname: string | null;
      providerName: string | null;
      forwardingDetected: boolean;
      sourceType: string | null;
      dkimAuthPassRate: number | null;
      spfAuthPassRate: number | null;
      // DKIM selectors used by this source
      dkimSelectors: string[];
      // SPF Mail From (envelope_from) domain
      envelopeFrom: string | null;
      envelopeFromDomains: string[];
      reportCount: number;
      totalMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
      firstSeen: string;
      lastSeen: string;
    }>;
    summary: {
      totalSources: number;
      totalMessages: number;
      dmarcCapableMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      forwarderMessages: number;
      forwarderSources: number;
    };
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    // Default to max limit (1000) to fetch more sources
    return this.request("GET", "/api/reports/by-source", {
      params: { limit: params?.limit ?? 1000, offset: params?.offset ?? 0, ...(params?.days ? { days: params.days } : {}) }
    });
  }

  // Get daily detail (per-date DKIM selector + SPF + disposition breakdown) for source IPs
  async getDailyDetail(ips: string[], days?: number): Promise<{ daily_detail: Record<string, DailyDetail[]> }> {
    return this.request("GET", "/api/reports/daily-detail", {
      params: { ips: ips.join(","), days: days ?? 30 },
    });
  }

  // Get analytics data for charts
  async getReportsAnalytics(params?: { days?: number; domainId?: string; startDate?: string; endDate?: string }): Promise<{
    timeSeries: Array<{
      date: string;
      totalMessages: number;
      dmarcPassRate: number | null;
      spfPassRate: number | null;
      dkimPassRate: number | null;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
    }>;
    topReporters: Array<{
      reporter: string;
      reportCount: number;
      totalMessages: number;
    }>;
    summary: {
      totalMessages: number;
      dmarcPassRate: number | null;
      spfPassRate: number | null;
      dkimPassRate: number | null;
      domainsWithData: number;
    };
    granularity: "daily" | "weekly";
    dateRange: {
      start: string;
      end: string;
      days: number;
    };
  }> {
    return this.request("GET", "/api/reports/analytics", { params });
  }

  // Remediation
  async getRemediationTasks(
    domainId?: string,
    status?: string,
    options?: { skipCache?: boolean; limit?: number; offset?: number }
  ): Promise<{ tasks: RemediationTask[]; total: number; limit: number; offset: number }> {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    // Default to a high limit to fetch all tasks, or use provided limit for pagination
    params.limit = options?.limit ?? 1000;
    if (options?.offset) params.offset = options.offset;

    if (domainId) {
      const baseKey = `GET:/api/domains/${domainId}/remediation-tasks?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`;
      return this.cachedRequest(
        this.cacheKey(baseKey),
        () => this.request("GET", `/api/domains/${domainId}/remediation-tasks`, { params }),
        options
      );
    }
    // Get all remediation tasks across all domains
    const baseKey = `GET:/api/remediation-tasks?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`;
    return this.cachedRequest(
      this.cacheKey(baseKey),
      () => this.request("GET", `/api/remediation-tasks`, { params }),
      options
    );
  }

  async updateRemediationTask(
    taskId: string,
    data: { status?: RemediationTask["status"]; notes?: string }
  ): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/remediation-tasks/${taskId}`, { body: data });
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  async completeTask(taskId: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/remediation-tasks/${taskId}/complete`);
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  async skipTask(taskId: string, reason?: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/remediation-tasks/${taskId}/skip`, {
      body: { reason },
    });
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  // User profile
  async getUserProfile(): Promise<{ profile: { id: string; name: string; email: string; role: string; created_at: string } }> {
    return this.request("GET", "/api/user/profile");
  }

  async updateUserProfile(data: { name?: string }): Promise<{ success: boolean }> {
    return this.request("PATCH", "/api/user/profile", { body: data });
  }

  // User preferences
  async getUserPreferences(): Promise<{ preferences: { weekly_digest: boolean; alert_emails: boolean; alert_severities?: string[] } }> {
    return this.request("GET", "/api/user/preferences");
  }

  async updateUserPreferences(
    prefs: Partial<{ weekly_digest: boolean; alert_emails: boolean; alert_severities: string[] }>
  ): Promise<{ preferences: { weekly_digest: boolean; alert_emails: boolean; alert_severities?: string[] } }> {
    return this.request("PATCH", "/api/user/preferences", { body: prefs });
  }

  // Events (Activity feed)
  async getEvents(params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
  }): Promise<{
    events: Array<{
      id: string;
      event_type: string;
      entity_type?: string;
      entity_id?: string;
      user_name?: string;
      description?: string;
      created_at: string;
    }>;
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    return this.request("GET", "/api/events", { params });
  }

  // Invitations
  async createInvitation(
    email: string,
    role: "admin" | "member" | "readonly" = "member"
  ): Promise<{ invitation: { id: string; email: string; role: string; expires_at: string } }> {
    return this.request("POST", "/api/invitations", { body: { email, role } });
  }

  async getInvitations(): Promise<{
    invitations: Array<{
      id: string;
      email: string;
      role: string;
      status: "pending" | "accepted" | "expired" | "revoked";
      expires_at: string;
      created_at: string;
    }>;
  }> {
    return this.request("GET", "/api/invitations");
  }

  async revokeInvitation(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/invitations/${id}`);
  }

  // Onboarding
  async completeOnboarding(): Promise<{ success: boolean }> {
    return this.request("POST", "/api/organization/complete-onboarding");
  }

  // Apex Links
  async getApexLinks(): Promise<{ apex_links: ApexLink[]; limits: { used: number; max: number | null } }> {
    return this.request("GET", "/api/apex-links");
  }

  async getApexLinkUsage(): Promise<{
    used: number;
    max: number;
    remaining: number;
    canGenerate: boolean;
    tier: string;
  }> {
    return this.request("GET", "/api/apex-links/usage");
  }

  async createApexLink(
    domainId: string,
    options?: {
      title?: string;
      sourceIds?: string[];
      providerIds?: string[];
      signingDomains?: string[];
      /**
       * APEX A1 (PR 45) — per-Source scope. When set, the public
       * /share/:token endpoint returns the Source-mirror payload
       * instead of the legacy domain dashboard. Mutually exclusive
       * with sourceIds / providerIds / signingDomains.
       */
      sourceId?: string;
      customMessage?: string;
      expiresInDays?: number;
    }
  ): Promise<{ apex_link: ApexLink; share_url: string }> {
    return this.request("POST", "/api/apex-links", {
      body: {
        domain_id: domainId,
        title: options?.title,
        source_ids: options?.sourceIds,
        provider_ids: options?.providerIds,
        signing_domains: options?.signingDomains,
        source_id: options?.sourceId,
        custom_message: options?.customMessage,
        expires_in_days: options?.expiresInDays,
      },
    });
  }

  async getApexLink(id: string): Promise<{ apex_link: ApexLink }> {
    return this.request("GET", `/api/apex-links/${id}`);
  }

  async revokeApexLink(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/apex-links/${id}`);
  }

  async getApexLinkAnalytics(id: string): Promise<{ analytics: ApexLinkAnalytics }> {
    return this.request("GET", `/api/apex-links/${id}/analytics`);
  }

  /**
   * APEX A2 (PR 46) — create a per-recipient scoped token under an
   * existing APEX link. Returns the recipient row + its share URL.
   */
  async createApexLinkRecipient(
    linkId: string,
    options?: {
      recipientName?: string;
      recipientEmail?: string;
      notes?: string;
    }
  ): Promise<{ recipient: ApexLinkRecipient; share_url: string }> {
    return this.request("POST", `/api/apex-links/${linkId}/recipients`, {
      body: {
        recipient_name: options?.recipientName,
        recipient_email: options?.recipientEmail,
        notes: options?.notes,
      },
    });
  }

  /** APEX A2 — list recipients (active + revoked) for a link. */
  async listApexLinkRecipients(
    linkId: string
  ): Promise<{ recipients: ApexLinkRecipient[] }> {
    return this.request("GET", `/api/apex-links/${linkId}/recipients`);
  }

  /** APEX A2 — revoke one recipient. Parent link unaffected. */
  async revokeApexLinkRecipient(
    linkId: string,
    recipientId: string
  ): Promise<{ success: boolean }> {
    return this.request(
      "DELETE",
      `/api/apex-links/${linkId}/recipients/${recipientId}`
    );
  }

  /**
   * APEX A2 — public read via a per-recipient scoped token. Used by
   * the public ShareDashboard at `/share/r/<recipientToken>`. Returns
   * the same source-mirror payload as `getPublicApexDashboard`, with
   * an extra `recipient` block carrying the (optional) name + open
   * counters.
   */
  async getPublicApexDashboardByRecipientToken(
    recipientToken: string
  ): Promise<PublicApexDashboardResponse> {
    return this.request("GET", `/api/share/r/${recipientToken}`);
  }

  // Public Apex dashboard (no auth required). Returns either the
  // legacy domain-dashboard shape (default) or the Source-mirror
  // shape (APEX A1, PR 45) when the link was created with `sourceId`.
  // Discriminated on the optional `kind` field — callers MUST check
  // kind before accessing kind-specific fields.
  async getPublicApexDashboard(
    token: string
  ): Promise<PublicApexDashboardResponse> {
    return this.request("GET", `/api/share/${token}`);
  }

  // Billing
  async getBilling(): Promise<{
    billing: {
      current_tier: string;
      subscription: {
        id: string;
        status: string;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
      } | null;
      payment_method: {
        brand: string;
        last4: string;
      } | null;
      overage: {
        base_domains: number;
        current_domains: number;
        overage_domains: number;
        overage_amount_cents: number;
        next_invoice_total_cents: number;
      } | null;
      invoices: Array<{
        id: string;
        amount_cents: number;
        status: string;
        date: string;
        pdf_url?: string;
      }>;
    };
  }> {
    return this.request("GET", "/api/billing");
  }

  async createSetupIntent(): Promise<{ client_secret: string }> {
    return this.request("POST", "/api/billing/setup-intent");
  }

  async subscribe(options: {
    tier: string;
    billing_cycle?: "monthly" | "annual";
    trial_days?: number;
    signup_flow?: boolean;
  }): Promise<{
    checkout_url: string;
    session_id: string;
    error?: string;
    code?: string;
  }> {
    return this.request("POST", "/api/billing/subscribe", {
      body: {
        tier: options.tier,
        billing_cycle: options.billing_cycle || "monthly",
        trial_days: options.trial_days,
        signup_flow: options.signup_flow,
      },
    });
  }

  async createPortalSession(): Promise<{
    portal_url?: string;
    error?: string;
    code?: string;
  }> {
    return this.request("POST", "/api/billing/portal");
  }

  /**
   * Reconcile the org's billing state with Stripe. Used when the
   * in-app tier doesn't match what the user is actually paying for —
   * usually because a webhook delivery failed. Returns before/after
   * state and any warnings about drift or fallback resolution.
   */
  async resyncBilling(): Promise<{
    before: {
      billing_tier: string;
      billing_status: string;
      billing_cycle: string | null;
      stripe_customer_id: string | null;
      stripe_subscription_id: string | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    };
    after: {
      billing_tier: string;
      billing_status: string;
      billing_cycle: string | null;
      stripe_customer_id: string | null;
      stripe_subscription_id: string | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    };
    changed: boolean;
    source_subscription: {
      id: string;
      status: "active" | "trialing" | "past_due" | "unpaid";
      current_period_end: string | null;
      cancel_at_period_end: boolean;
      tier: string;
    } | null;
    warnings: string[];
  }> {
    return this.request("POST", "/api/billing/resync");
  }

  // Session Management
  async logout(): Promise<{ success: boolean }> {
    return this.request("POST", "/auth/logout");
  }

  async refreshToken(): Promise<{ token: string }> {
    return this.request("POST", "/auth/refresh", { skipTokenRefresh: true });
  }

  async logoutAll(): Promise<{ success: boolean }> {
    return this.request("POST", "/auth/logout-all");
  }

  async getSessions(): Promise<{
    sessions: Array<{
      id: string;
      device_info: string | null;
      ip_address: string | null;
      created_at: string;
      last_activity_at: string;
      expires_at: string;
    }>;
  }> {
    return this.request("GET", "/auth/sessions");
  }

  async revokeSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/auth/sessions/${sessionId}`);
  }

  // Account Deletion
  async requestAccountDeletion(reason?: string): Promise<{
    request_id: string;
    scheduled_deletion_at: string;
    message: string;
  }> {
    return this.request("POST", "/api/account/delete-request", {
      body: reason ? { reason } : {},
    });
  }

  async cancelAccountDeletion(): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/account/cancel-deletion");
  }

  async getAccountDeletionStatus(): Promise<{
    deletion_request: {
      id: string;
      status: "pending" | "processing" | "completed" | "canceled";
      reason: string | null;
      requested_at: string;
      scheduled_deletion_at: string;
      canceled_at: string | null;
    } | null;
  }> {
    return this.request("GET", "/api/account/deletion-status");
  }

  // DNS Integrations
  async getDnsIntegrations(): Promise<{ integrations: DnsIntegration[] }> {
    return this.request("GET", "/api/dns-integrations");
  }

  async getDnsIntegration(id: string): Promise<{ integration: DnsIntegration }> {
    return this.request("GET", `/api/dns-integrations/${id}`);
  }

  async connectGoDaddy(data: {
    apiKey: string;
    apiSecret: string;
    name?: string;
    domainId?: string;
  }): Promise<{ integration: DnsIntegration }> {
    return this.request("POST", "/api/dns-integrations/godaddy", { body: data });
  }

  async testDnsIntegration(id: string): Promise<{
    success: boolean;
    error?: string;
    zones?: DnsZone[];
  }> {
    return this.request("POST", `/api/dns-integrations/${id}/test`);
  }

  async deleteDnsIntegration(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/dns-integrations/${id}`);
  }

  // Domain DNS Integration
  async getDomainDnsIntegration(domainId: string): Promise<{
    integration: DnsIntegration | null;
    domain: { id: string; name: string };
  }> {
    return this.request("GET", `/api/domains/${domainId}/dns-integration`);
  }

  async detectDnsProvider(domainId: string): Promise<DnsDetectionResult> {
    return this.request("GET", `/api/domains/${domainId}/detect-provider`);
  }

  async getDomainDnsRecords(
    domainId: string,
    type?: string
  ): Promise<{ records: DnsRecord[] }> {
    return this.request("GET", `/api/domains/${domainId}/dns-records`, {
      params: type ? { type } : {},
    });
  }

  async createDnsRecord(
    domainId: string,
    record: { type: "TXT" | "CNAME" | "MX"; name: string; content: string; ttl?: number }
  ): Promise<{ record: DnsRecord }> {
    return this.request("POST", `/api/domains/${domainId}/dns-records`, {
      body: record,
    });
  }

  async getDomainDnsOperations(
    domainId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{
    operations: DnsOperation[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    return this.request("GET", `/api/domains/${domainId}/dns-operations`, { params });
  }

  // Apply DNS from remediation task (for GoDaddy/API-based integrations)
  async applyRemediationDns(taskId: string): Promise<{
    success: boolean;
    record?: DnsRecord;
    operationType?: "create" | "update";
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/apply-dns`);
  }

  // ============================================
  // Domain Connect (for Cloudflare)
  // ============================================

  // Get Domain Connect URL for DMARC
  async getDomainConnectDmarcUrl(params: {
    domain: string;
    policy: "none" | "quarantine" | "reject";
    rua?: string;
    pct?: string;
    redirect_uri?: string;
    state?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/dmarc", { params });
  }

  // Get Domain Connect URL for SPF
  async getDomainConnectSpfUrl(params: {
    domain: string;
    spf_record: string;
    redirect_uri?: string;
    state?: string;
    /**
     * Sources Spine PR 26 follow-up — when the redirect originates
     * from the Source detail "Apply via Cloudflare" button, pass the
     * source_id so the backend can insert a pending
     * remediation_verifications row before handing off. Without it
     * the redirect still works; the close-the-loop card just won't
     * surface on the Source's Remediation tab afterwards.
     */
    source_id?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/spf", { params });
  }

  // Get Domain Connect URL for domain verification
  async getDomainConnectVerifyUrl(params: {
    domain: string;
    token: string;
    redirect_uri?: string;
    state?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/verify", { params });
  }

  // List available Domain Connect templates
  async getDomainConnectTemplates(): Promise<{
    providerId: string;
    templates: Array<{
      serviceId: string;
      name: string;
      description: string;
      variables: Array<{ name: string; description: string; required: boolean; default?: string }>;
      records: Array<{ type: string; host: string; data?: string; ttl?: number }>;
    }>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/templates");
  }

  // Apply DNS via Domain Connect for remediation task (returns URL for user to approve in Cloudflare)
  async applyRemediationDomainConnect(
    taskId: string,
    options?: { redirect_uri?: string; state?: string }
  ): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
    task: { id: string; type: string; domain: string };
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/apply-domain-connect`, {
      body: options || {},
    });
  }

  // ============================================
  // API Keys
  // ============================================

  // List all API keys for the organization
  async getApiKeys(): Promise<{
    keys: Array<{
      id: string;
      name: string;
      keyPreview: string;
      scopes: string[];
      lastUsedAt: string | null;
      expiresAt: string | null;
      revokedAt: string | null;
      createdAt: string;
      createdBy: string | null;
    }>;
  }> {
    return this.request("GET", "/api/keys");
  }

  // Create a new API key
  async createApiKey(params: {
    name: string;
    scopes?: string[];
    expiresInDays?: number;
  }): Promise<{
    key: {
      id: string;
      name: string;
      apiKey: string;
      keyPreview: string;
      scopes: string[];
      expiresAt: string | null;
      createdAt: string;
    };
    warning: string;
  }> {
    return this.request("POST", "/api/keys", { body: params });
  }

  // Revoke an API key
  async revokeApiKey(keyId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/keys/${keyId}`);
  }

  // ============================================
  // Analytics & Feature Tracking
  // ============================================

  /**
   * Track when a user attempts to access a gated feature.
   * Returns whether the feature was allowed based on their tier.
   */
  async trackFeatureUsage(params: {
    feature: string;
    context?: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    wasAllowed: boolean;
    requiredTier?: string;
  }> {
    return this.request("POST", "/api/analytics/feature-usage", { body: params });
  }

  /**
   * Track when an upgrade prompt is shown to a user.
   * Returns a promptId for tracking clicks and conversions.
   */
  async trackUpgradePromptShown(params: {
    feature: string;
    location: string;
    targetTier: string;
  }): Promise<{
    success: boolean;
    promptId: string;
  }> {
    return this.request("POST", "/api/analytics/upgrade-prompt/shown", { body: params });
  }

  /**
   * Track when a user clicks the upgrade button on a prompt.
   */
  async trackUpgradePromptClick(promptId: string): Promise<{ success: boolean }> {
    return this.request("POST", "/api/analytics/upgrade-prompt/click", {
      body: { promptId },
    });
  }

  /**
   * Track when a user completes an upgrade (conversion).
   */
  async trackConversion(params: {
    promptId?: string;
    previousTier: string;
    newTier: string;
  }): Promise<{
    success: boolean;
    promptsUpdated: number;
  }> {
    return this.request("POST", "/api/analytics/conversion", { body: params });
  }

  /**
   * Get feature usage statistics for the organization.
   */
  async getFeatureUsageStats(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    stats: Array<{
      featureName: string;
      totalAttempts: number;
      allowedAttempts: number;
      blockedAttempts: number;
      uniqueUsers: number;
    }>;
  }> {
    return this.request("GET", "/api/analytics/feature-usage/stats", {
      params: period ? { period } : {},
    });
  }

  /**
   * Get upgrade prompt effectiveness stats.
   */
  async getUpgradePromptStats(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    stats: Array<{
      location: string;
      feature: string;
      impressions: number;
      clicks: number;
      conversions: number;
      clickThroughRate: number;
      conversionRate: number;
    }>;
  }> {
    return this.request("GET", "/api/analytics/upgrade-prompts/stats", {
      params: period ? { period } : {},
    });
  }

  /**
   * Get recently blocked features for insights.
   */
  async getBlockedFeatures(): Promise<{
    features: Array<{
      feature: string;
      count: number;
      lastAttempt: string;
    }>;
  }> {
    return this.request("GET", "/api/analytics/blocked-features");
  }

  // ============================================
  // UX Analytics (Page Views, Feature Discovery, etc.)
  // ============================================

  /**
   * Track a page view
   */
  async trackPageView(params: {
    sessionId: string;
    pagePath: string;
    pageName: string;
    referrerPath?: string | null;
    viewportWidth?: number;
    isMobile?: boolean;
  }): Promise<{ success: boolean; id: string }> {
    return this.request("POST", "/api/analytics/ux/page-view", { body: params });
  }

  /**
   * Update time spent on a page (called when navigating away)
   */
  async updatePageViewTime(
    pageViewId: string,
    timeOnPageMs: number
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/analytics/ux/page-view/${pageViewId}`, {
      body: { timeOnPageMs },
    });
  }

  /**
   * Track a help interaction
   */
  async trackHelpInteraction(params: {
    sessionId: string;
    interactionType: string;
    sourcePage?: string;
    searchQuery?: string;
    helpTopic?: string;
  }): Promise<{ success: boolean; id: string }> {
    return this.request("POST", "/api/analytics/ux/help", { body: params });
  }

  /**
   * Track feature discovery (first-time access)
   */
  async trackFeatureDiscovery(params: {
    featureKey: string;
    discoveryPath?: string;
    sourcePage?: string;
  }): Promise<{ success: boolean; isFirstDiscovery: boolean; id: string | null }> {
    return this.request("POST", "/api/analytics/ux/feature-discovery", {
      body: params,
    });
  }

  /**
   * Track a journey milestone
   */
  async trackMilestone(params: {
    milestone: string;
    domainId?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ success: boolean; isFirstTime: boolean; eventId: string | null }> {
    return this.request("POST", "/api/analytics/ux/milestone", { body: params });
  }

  /**
   * Get onboarding progress
   */
  async getOnboardingProgress(): Promise<{
    progress: {
      organizationId: string;
      steps: {
        addDomain: boolean;
        verifyDns: boolean;
        reviewSources: boolean;
        completeTask: boolean;
        inviteTeam: boolean;
      };
      checklistDismissed: boolean;
      dismissedAt: string | null;
      completedAt: string | null;
      completedCount: number;
      totalCount: number;
      showChecklist: boolean;
    };
    nextStep: {
      id: string;
      label: string;
      description: string;
      completed: boolean;
      requiredFor: "all" | "paid";
    } | null;
  }> {
    return this.request("GET", "/api/analytics/ux/onboarding");
  }

  /**
   * Dismiss the onboarding checklist
   */
  async dismissOnboardingChecklist(): Promise<{ success: boolean }> {
    return this.request("POST", "/api/analytics/ux/onboarding/dismiss");
  }

  /**
   * Get UX metrics summary (admin only)
   */
  async getUXMetrics(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    summary: {
      totalSessions: number;
      avgPagesPerSession: number | null;
      helpPageVisits: number;
      helpSearches: number;
      tasksCreated: number;
      tasksCompleted: number;
      taskCompletionRate: number | null;
      avgTimeToCompleteHours: number | null;
      featuresDiscoveredIn7d: number;
    };
    journeyMilestones: Array<{
      milestone: string;
      days_since_signup: number;
    }>;
    topPages: Array<{
      page_path: string;
      page_name: string;
      views: number;
      unique_users: number;
      avg_time: number | null;
    }>;
  }> {
    return this.request("GET", "/api/analytics/ux/metrics", {
      params: period ? { period } : {},
    });
  }

  // ============================================
  // Audit & Compliance (Enterprise+)
  // ============================================

  /**
   * Get available audit report types for the organization.
   */
  async getAuditReportTypes(): Promise<{
    available: boolean;
    tier: string;
    report_types: Array<{
      id: string;
      name: string;
      description: string;
      formats: string[];
    }>;
    upgrade_message: string | null;
  }> {
    return this.request("GET", "/api/audit/report-types");
  }

  /**
   * Get audit event type reference.
   */
  async getAuditEventTypes(): Promise<{
    event_types: Array<{
      type: string;
      category: string;
      description: string;
    }>;
  }> {
    return this.request("GET", "/api/audit/event-types");
  }

  /**
   * Export audit logs as JSON.
   */
  async exportAuditJson(options?: {
    start_date?: string;
    end_date?: string;
    event_types?: string[];
    limit?: number;
    offset?: number;
  }): Promise<{
    events: Array<{
      id: string;
      event_type: string;
      entity_type: string | null;
      entity_id: string | null;
      description: string | null;
      metadata: Record<string, unknown>;
      ip_address: string | null;
      created_at: string;
      user_name: string | null;
      user_email: string | null;
    }>;
    export_metadata: {
      organization_id: string;
      exported_at: string;
      exported_by: string;
      filters: {
        start_date: string | null;
        end_date: string | null;
        event_types: string[] | null;
      };
      pagination: {
        total: number;
        limit: number;
        offset: number;
      };
    };
  }> {
    const params = new URLSearchParams();
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);
    if (options?.event_types?.length) params.set("event_types", options.event_types.join(","));
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());

    const query = params.toString();
    return this.request("GET", `/api/audit/export/json${query ? `?${query}` : ""}`);
  }

  /**
   * Get CSV export URL for audit logs.
   */
  getAuditCsvUrl(options?: {
    start_date?: string;
    end_date?: string;
    event_types?: string[];
    max_rows?: number;
  }): string {
    const params = new URLSearchParams();
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);
    if (options?.event_types?.length) params.set("event_types", options.event_types.join(","));
    if (options?.max_rows) params.set("max_rows", options.max_rows.toString());

    const query = params.toString();
    return `${API_BASE}/api/audit/export/csv${query ? `?${query}` : ""}`;
  }

  /**
   * Get PDF report URL.
   */
  getAuditPdfUrl(options?: {
    type?: "audit" | "compliance" | "executive";
    start_date?: string;
    end_date?: string;
  }): string {
    const params = new URLSearchParams();
    if (options?.type) params.set("type", options.type);
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);

    const query = params.toString();
    return `${API_BASE}/api/audit/report/pdf${query ? `?${query}` : ""}`;
  }

  /**
   * Get HTML preview URL for report (for testing).
   */
  getAuditPreviewUrl(options?: {
    type?: "audit" | "compliance" | "executive";
    start_date?: string;
    end_date?: string;
  }): string {
    const params = new URLSearchParams();
    if (options?.type) params.set("type", options.type);
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);

    const query = params.toString();
    return `${API_BASE}/api/audit/report/preview${query ? `?${query}` : ""}`;
  }

  // ============================================
  // IP Access Whitelist (Enterprise)
  // ============================================

  /**
   * Get IP restriction status for the organization.
   */
  async getIpWhitelistStatus(): Promise<{
    enabled: boolean;
    activeEntries: number;
    canManage: boolean;
    tier: string;
    currentIp: string;
  }> {
    return this.request("GET", "/api/ip-whitelist/status");
  }

  /**
   * Get all IP whitelist entries.
   */
  async getIpWhitelistEntries(options?: { include_expired?: boolean }): Promise<{
    entries: IpWhitelistEntry[];
    enabled: boolean;
    currentIp: string;
    tier: string;
    permissions: { canManageIpWhitelist: boolean };
  }> {
    return this.request("GET", "/api/ip-whitelist", {
      params: options?.include_expired ? { include_expired: "true" } : {},
    });
  }

  /**
   * Toggle IP restriction on/off.
   */
  async toggleIpRestriction(enabled: boolean): Promise<{
    success: boolean;
    enabled: boolean;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist/toggle", {
      body: { enabled },
    });
  }

  /**
   * Add an IP address to the whitelist.
   */
  async addIpToWhitelist(params: {
    ip_address: string;
    description?: string;
    expires_at?: string;
  }): Promise<{
    success: boolean;
    id: string;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist", { body: params });
  }

  /**
   * Add current IP to the whitelist.
   */
  async addCurrentIpToWhitelist(description?: string): Promise<{
    success: boolean;
    id: string;
    ip_address: string;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist/add-current", {
      body: { description },
    });
  }

  /**
   * Update an IP whitelist entry.
   */
  async updateIpWhitelistEntry(
    id: string,
    params: { description?: string; expires_at?: string | null; is_active?: boolean }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/ip-whitelist/${id}`, { body: params });
  }

  /**
   * Delete an IP from the whitelist.
   */
  async deleteIpFromWhitelist(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/ip-whitelist/${id}`);
  }

  // ============================================
  // SPF Optimization (AI-Powered)
  // ============================================

  /**
   * Resolve an SPF record to get full DNS lookup data
   */
  async resolveSpf(domainId: string): Promise<SpfResolveResult> {
    return this.request("GET", `/api/spf/${domainId}/resolution`);
  }

  /**
   * Run AI-powered SPF optimization
   */
  async optimizeSpf(
    domainId: string,
    params: {
      current_spf: string | null;
      resolved_data?: SpfResolveResult;
      optimization_goals?: string[];
    }
  ): Promise<SpfOptimizationResult> {
    return this.request("POST", `/api/spf/${domainId}/optimize`, {
      body: params,
    });
  }

  /**
   * Apply optimized SPF record
   */
  async applySpf(
    domainId: string,
    params: {
      optimization_id: string;
      method: "cloudflare" | "godaddy" | "domain_connect";
      redirect_uri?: string;
    }
  ): Promise<{ success: boolean; record?: DnsRecord }> {
    return this.request("POST", `/api/spf/${domainId}/apply`, {
      body: params,
    });
  }

  /**
   * Get SPF optimization history for a domain
   */
  async getSpfOptimizationHistory(
    domainId: string
  ): Promise<{
    optimizations: Array<{
      id: string;
      current_spf: string | null;
      suggested_spf: string;
      applied_at: string | null;
      lookup_count_before: number;
      lookup_count_after: number;
      risk_level: string;
      status: string;
    }>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    return this.request("GET", `/api/spf/${domainId}/optimizations`);
  }

  // ============================================
  // MSP (Managed Service Provider) Routes
  // ============================================

  // Get MSP dashboard summary
  async getMspDashboard(): Promise<{
    summary: {
      total_clients: number;
      total_domains: number;
      total_users: number;
    };
    stats: {
      total_messages: number;
      avg_pass_rate: number;
      total_pass: number;
      total_fail: number;
    };
    clientsWithIssues: Array<{
      id: string;
      name: string;
      open_alerts: number;
      avg_pass_rate: number;
    }>;
    revenue: {
      estimated_monthly_cents: number;
    };
    partner: {
      tier: "reseller" | "white_label";
      wholesale_rate_cents: number;
    };
    profitability: {
      your_revenue_cents: number;
      verkh_cost_cents: number;
      margin_cents: number;
      margin_percent: number;
    };
  }> {
    return this.request("GET", "/api/msp/dashboard");
  }

  // List MSP client organizations
  async getMspClients(): Promise<{ clients: MspClientOrganization[] }> {
    return this.request("GET", "/api/msp/clients");
  }

  // Create a new MSP client organization
  async createMspClient(data: {
    name: string;
    billing_rate_cents_per_domain?: number;
    minimum_monthly_cents?: number;
  }): Promise<{ client: MspClientOrganization }> {
    return this.request("POST", "/api/msp/clients", { body: data });
  }

  // Get specific MSP client details
  async getMspClient(clientId: string): Promise<{
    client: MspClientOrganization;
    domains: Domain[];
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}`);
  }

  // Update MSP client
  async updateMspClient(
    clientId: string,
    data: {
      name?: string;
      rateCentsPerDomain?: number;
      minimumMonthlyCents?: number;
      notes?: string;
    }
  ): Promise<{ client: MspClientOrganization }> {
    return this.request("PATCH", `/api/msp/clients/${clientId}`, { body: data });
  }

  // Delete MSP client
  async deleteMspClient(clientId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/clients/${clientId}`);
  }

  // Get domains for an MSP client
  async getMspClientDomains(clientId: string): Promise<{ domains: Domain[] }> {
    return this.request("GET", `/api/msp/clients/${clientId}/domains`);
  }

  // Add domain to MSP client
  async addMspClientDomain(
    clientId: string,
    domainName: string
  ): Promise<{ domain: Domain }> {
    return this.request("POST", `/api/msp/clients/${clientId}/domains`, {
      body: { domain_name: domainName },
    });
  }

  // Get MSP billing summary
  async getMspBillingSummary(): Promise<{
    summary: {
      total_monthly_revenue_cents: number;
      total_domains: number;
      client_count: number;
    };
    clients: Array<{
      client_id: string;
      client_name: string;
      rate_cents_per_domain: number;
      minimum_monthly_cents: number;
      domain_count: number;
      monthly_total_cents: number;
    }>;
  }> {
    return this.request("GET", "/api/msp/billing/summary");
  }

  // Get invoice billing summary (current month)
  async getMspInvoiceSummary(): Promise<{
    currentMonth: {
      clients: Array<{
        client_id: string;
        client_name: string;
        rate_cents_per_domain: number;
        minimum_monthly_cents: number;
        domain_count: number;
        current_month_cents: number;
      }>;
      total_cents: number;
    };
  }> {
    return this.request("GET", "/api/msp/invoices");
  }

  // List MSP invoices with pagination and filters
  async getMspInvoices(filters?: {
    status?: string;
    clientId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ invoices: MspInvoice[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.clientId) params.set("clientId", filters.clientId);
    if (filters?.limit) params.set("limit", String(filters.limit));
    if (filters?.offset) params.set("offset", String(filters.offset));
    const query = params.toString();
    return this.request("GET", `/api/msp/invoices/list${query ? `?${query}` : ""}`);
  }

  // Get single invoice
  async getMspInvoice(invoiceId: string): Promise<{ invoice: MspInvoice }> {
    return this.request("GET", `/api/msp/invoices/${invoiceId}`);
  }

  // Generate invoice for client
  async generateMspInvoice(data: {
    clientId: string;
    periodStart: string;
    periodEnd: string;
    dueDate?: string;
    notes?: string;
    lineItems?: MspInvoiceLineItem[];
    taxCents?: number;
  }): Promise<{ invoice: MspInvoice }> {
    return this.request("POST", "/api/msp/invoices/generate", { body: data });
  }

  // Update invoice
  async updateMspInvoice(
    invoiceId: string,
    data: {
      status?: "draft" | "sent" | "paid" | "cancelled";
      lineItems?: MspInvoiceLineItem[];
      notes?: string;
      taxCents?: number;
      dueDate?: string;
    }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/invoices/${invoiceId}`, { body: data });
  }

  // Delete draft invoice
  async deleteMspInvoice(invoiceId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/invoices/${invoiceId}`);
  }

  // Get invoice PDF download URL
  getMspInvoicePdfUrl(invoiceId: string): string {
    return `${API_BASE}/api/msp/invoices/${invoiceId}/pdf`;
  }

  // Get invoice export URL (CSV for various accounting platforms)
  getMspInvoiceExportUrl(options: {
    format: "csv" | "quickbooks" | "xero";
    periodStart?: string;
    periodEnd?: string;
    status?: string;
  }): string {
    const params = new URLSearchParams();
    params.set("format", options.format);
    if (options.periodStart) params.set("periodStart", options.periodStart);
    if (options.periodEnd) params.set("periodEnd", options.periodEnd);
    if (options.status) params.set("status", options.status);
    return `${API_BASE}/api/msp/invoices/export?${params.toString()}`;
  }

  // Send invoice via email
  async sendMspInvoice(
    invoiceId: string,
    data?: {
      recipientEmail?: string;
      recipientName?: string;
    }
  ): Promise<{
    success: boolean;
    messageId?: string;
    sentTo: string;
    sentFrom: string;
    usedCustomDomain: boolean;
  }> {
    return this.request("POST", `/api/msp/invoices/${invoiceId}/send`, { body: data || {} });
  }

  // ============================================
  // MSP Email Domain APIs
  // ============================================

  // List verified email domains
  async getMspEmailDomains(): Promise<{ domains: MspEmailDomain[] }> {
    return this.request("GET", "/api/msp/email-domains");
  }

  // Add email domain for verification
  async addMspEmailDomain(data: {
    domain: string;
    defaultFromEmail?: string;
    defaultFromName?: string;
  }): Promise<{ domain: MspEmailDomain }> {
    return this.request("POST", "/api/msp/email-domains", { body: data });
  }

  // Verify email domain (check DNS records)
  async verifyMspEmailDomain(domainId: string): Promise<{
    status: "verified" | "pending" | "failed";
    dns_records: MspDnsRecord[];
  }> {
    return this.request("POST", `/api/msp/email-domains/${domainId}/verify`);
  }

  // Update email domain settings
  async updateMspEmailDomain(
    domainId: string,
    data: {
      defaultFromEmail?: string;
      defaultFromName?: string;
    }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/email-domains/${domainId}`, { body: data });
  }

  // Delete email domain
  async deleteMspEmailDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/email-domains/${domainId}`);
  }

  // ============================================
  // MSP App Domains APIs (Custom white-label domains)
  // ============================================

  // Get all app domains for MSP
  async getMspAppDomains(): Promise<{ domains: MspAppDomain[] }> {
    return this.request("GET", "/api/msp/app-domains");
  }

  // Add app domain for verification
  async addMspAppDomain(data: { domain: string }): Promise<{ domain: MspAppDomain }> {
    return this.request("POST", "/api/msp/app-domains", { body: data });
  }

  // Verify app domain (check DNS records)
  async verifyMspAppDomain(domainId: string): Promise<{
    status: "verified" | "pending" | "failed";
    ssl_status?: string;
    dns_records: MspDnsRecord[];
  }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/verify`);
  }

  // Delete app domain
  async deleteMspAppDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/app-domains/${domainId}`);
  }

  // Set app domain as primary
  async setMspAppDomainPrimary(domainId: string): Promise<{ success: boolean }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/set-primary`);
  }

  // Refresh SSL status from Cloudflare
  async refreshMspAppDomainSsl(domainId: string): Promise<{
    ssl_status: "pending" | "active" | "expired";
    cf_ssl_status?: string;
    ssl_validation_records: SslValidationRecord[];
  }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/refresh-ssl`);
  }

  // ============================================
  // Public Branding API (for white-label domains)
  // ============================================

  // Get branding for current hostname (used on custom domains)
  async getBranding(): Promise<{
    type: "default" | "white_label";
    organizationId?: string;
    organizationName: string;
    logoUrl: string | null;
    primaryColor: string | null;
    customDomain?: string | null;
  }> {
    return this.request("GET", "/api/branding");
  }

  // ============================================
  // MSP Branding APIs
  // ============================================

  // Get MSP branding configuration
  async getMspBranding(): Promise<{ branding: MspBrandingConfig }> {
    return this.request("GET", "/api/msp/branding");
  }

  // Update MSP branding configuration
  async updateMspBranding(
    data: Partial<MspBrandingConfig>
  ): Promise<{ branding: MspBrandingConfig; message: string }> {
    return this.request("PATCH", "/api/msp/branding", { body: data });
  }

  // Get branding for a specific MSP client
  async getMspClientBranding(clientId: string): Promise<{
    branding: MspBrandingConfig;
    inherited_from_msp: boolean;
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}/branding`);
  }

  // Update branding for a specific MSP client
  async updateMspClientBranding(
    clientId: string,
    data: Partial<MspBrandingConfig>
  ): Promise<{ branding: MspBrandingConfig; message: string }> {
    return this.request("PATCH", `/api/msp/clients/${clientId}/branding`, { body: data });
  }

  // ============================================
  // Team Invitations
  // ============================================

  // Invite a team member to the organization
  async inviteTeamMember(
    email: string,
    role: "admin" | "member",
    options?: { isMspAdmin?: boolean }
  ): Promise<{ success: boolean; invitation: { id: string; email: string } }> {
    return this.request("POST", "/api/invitations", {
      body: { email, role, is_msp_admin: options?.isMspAdmin },
    });
  }

  // ============================================
  // MSP Team Management APIs
  // ============================================

  // Get team members with assignment counts
  async getMspTeam(): Promise<{ team: MspTeamMember[]; total: number }> {
    return this.request("GET", "/api/msp/team");
  }

  // Get specific team member with their assignments
  async getMspTeamMember(userId: string): Promise<{
    member: MspTeamMember;
    assignments: MspClientAssignment[];
  }> {
    return this.request("GET", `/api/msp/team/${userId}`);
  }

  // Update team member (e.g., is_msp_admin flag)
  async updateMspTeamMember(
    userId: string,
    data: { is_msp_admin?: boolean }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/team/${userId}`, { body: data });
  }

  // Remove team member from MSP
  async removeMspTeamMember(userId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/team/${userId}`);
  }

  // Get assignments for a team member
  async getMspTeamMemberAssignments(userId: string): Promise<{
    assignments: MspClientAssignment[];
  }> {
    return this.request("GET", `/api/msp/team/${userId}/assignments`);
  }

  // Bulk assign team member to clients
  async assignMspTeamMemberToClients(
    userId: string,
    data: { assignments: Array<{ client_id: string; access_level: string }> }
  ): Promise<{ success: boolean; created: number; updated: number }> {
    return this.request("POST", `/api/msp/team/${userId}/assignments`, { body: data });
  }

  // Remove team member assignment from a client
  async removeMspTeamMemberAssignment(
    userId: string,
    clientId: string
  ): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/team/${userId}/assignments/${clientId}`);
  }

  // Get team members assigned to a specific client
  async getMspClientTeam(clientId: string): Promise<{
    team: Array<MspTeamMember & { access_level: string }>;
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}/team`);
  }

  // ============================================
  // SSO Configuration APIs
  // ============================================

  // Get SSO configuration for current organization
  async getSsoConfig(): Promise<{
    config: SsoConfiguration | null;
    spSettings: { entityId: string; acsUrl: string; metadataUrl: string };
    emailDomains: SsoEmailDomain[];
  }> {
    return this.request("GET", "/api/sso/config");
  }

  // Configure SAML SSO
  async configureSamlSso(data: {
    providerName?: string;
    idpEntityId: string;
    idpSsoUrl: string;
    idpSloUrl?: string;
    idpCertificate: string;
    attributeMapping?: Record<string, string>;
    roleMapping?: Record<string, string[]>;
    defaultRole?: string;
    mspGroupMapping?: Record<string, { client_id: string; access_level: string }>;
    jitProvisioning?: boolean;
    allowIdpInitiated?: boolean;
  }): Promise<{
    success: boolean;
    spSettings: { entityId: string; acsUrl: string; metadataUrl: string };
  }> {
    return this.request("PUT", "/api/sso/saml", { body: data });
  }

  // Configure OIDC SSO
  async configureOidcSso(data: {
    providerName?: string;
    oidcIssuer: string;
    oidcClientId: string;
    oidcClientSecret: string;
    oidcScopes?: string;
    attributeMapping?: Record<string, string>;
    roleMapping?: Record<string, string[]>;
    defaultRole?: string;
    mspGroupMapping?: Record<string, { client_id: string; access_level: string }>;
    jitProvisioning?: boolean;
  }): Promise<{ success: boolean; redirectUrl: string }> {
    return this.request("PUT", "/api/sso/oidc", { body: data });
  }

  // Enable/disable SSO
  async setSsoEnabled(enabled: boolean): Promise<{ success: boolean; enabled: boolean }> {
    return this.request("PUT", "/api/sso/enable", { body: { enabled } });
  }

  // Enforce SSO (disable other login methods)
  async setSsoEnforced(enforce: boolean): Promise<{ success: boolean; enforce: boolean }> {
    return this.request("PUT", "/api/sso/enforce", { body: { enforce } });
  }

  // Get SSO login history
  async getSsoLoginHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ attempts: SsoLoginAttempt[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    return this.request("GET", `/api/sso/login-history?${query}`);
  }

  // Add email domain for SSO
  async addSsoEmailDomain(emailDomain: string): Promise<{
    id: string;
    emailDomain: string;
    verified: boolean;
    verificationToken: string;
    verificationMethod: string;
  }> {
    return this.request("POST", "/api/sso/email-domain", { body: { emailDomain } });
  }

  // Verify email domain
  async verifySsoEmailDomain(domainId: string): Promise<{
    success: boolean;
    verified: boolean;
    message?: string;
  }> {
    return this.request("POST", `/api/sso/email-domain/${domainId}/verify`);
  }

  // Remove email domain
  async removeSsoEmailDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/sso/email-domain/${domainId}`);
  }

  // ============================================
  // Development/Testing APIs (non-production only)
  // ============================================

  // Enable MSP mode for current organization (DEV ONLY)
  async devEnableMsp(partnerTier: "reseller" | "white_label" = "white_label"): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/enable", { body: { partner_tier: partnerTier } });
  }

  // Set partner tier for current MSP organization (DEV ONLY)
  async devSetPartnerTier(partnerTier: "reseller" | "white_label"): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/set-tier", { body: { partner_tier: partnerTier } });
  }

  // Seed test MSP clients with sample data (DEV ONLY)
  async devSeedMspClients(): Promise<{
    success: boolean;
    message: string;
    clients: Array<{
      id: string;
      name: string;
      domains: Array<{ id: string; domain_name: string }>;
      rate_cents_per_domain: number;
      minimum_monthly_cents: number;
    }>;
  }> {
    return this.request("POST", "/api/dev/msp/seed-clients");
  }

  // Disable MSP mode and remove test data (DEV ONLY)
  async devDisableMsp(): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/disable");
  }

  // ============================================
  // AI Recommendations (Proactive AI Insights)
  // ============================================

  // Get proactive AI recommendations from cached insights
  async getAIRecommendations(): Promise<AIRecommendationsResponse> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/ai/recommendations"),
      () => this.request("GET", "/api/ai/recommendations"),
      { ttl: 60000 } // 1 minute cache
    );
  }

  // Approve an AI classification
  async approveAIClassification(sourceId: string): Promise<{
    success: boolean;
    sourceId: string;
    classification: string;
    message: string;
  }> {
    const result = await this.request<{
      success: boolean;
      sourceId: string;
      classification: string;
      message: string;
    }>("POST", `/api/ai/recommendations/${sourceId}/approve`);
    // Clear AI recommendations cache after action
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  // Reject an AI classification and optionally set a new one
  async rejectAIClassification(
    sourceId: string,
    options?: {
      newCategory?: string;
      newSubtype?: string;
      newStatus?: string;
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    sourceId: string;
    newCategory?: string;
    newSubtype?: string;
    newStatus?: string;
    message: string;
  }> {
    const result = await this.request<{
      success: boolean;
      sourceId: string;
      newCategory?: string;
      newSubtype?: string;
      newStatus?: string;
      message: string;
    }>("POST", `/api/ai/recommendations/${sourceId}/reject`, {
      body: options || {},
    });
    // Clear AI recommendations cache after action
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  // ============================================
  // Action Items Summary
  // ============================================

  async getActionItemsSummary(domainId?: string, limit?: number): Promise<ActionItemsSummary> {
    const params: Record<string, string | number> = {};
    if (domainId) params.domainId = domainId;
    if (limit !== undefined) params.limit = limit;
    const queryString = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();
    const baseKey = `GET:/api/action-items/summary${queryString ? `?${queryString}` : ""}`;
    return this.cachedRequest(
      this.cacheKey(baseKey),
      () => this.request("GET", "/api/action-items/summary", Object.keys(params).length > 0 ? { params } : undefined),
      { ttl: 60000 }
    );
  }

  // ============================================
  // Action Items (unified, post-migration-0081)
  // ============================================

  /**
   * Unified action-items list (Phase 1 PR 14). Returns the union of
   * event rows from the `action_items` table AND standing-condition
   * rows computed from the spine's source state (Unclassified or
   * Approved-Misconfigured sources, with trailing-window aggregates).
   *
   * Filter standing rows out via `kind: "alert,task,insight"`, or
   * scope to standing-only via `kind: "standing"`. `windowDays` drives
   * the per-standing-row aggregate; clamped [1,90], default 30.
   *
   * The response includes a `counts` block with standing/event/total
   * so the page knows how many of each kind landed in the merged set
   * without filtering client-side.
   */
  async getActionItems(opts: {
    domainId?: string;
    limit?: number;
    offset?: number;
    /** CSV: active,snoozed,completed,dismissed,not_applicable */
    status?: string;
    /** CSV: insight,alert,task,standing */
    kind?: string;
    /** Trailing window for standing-row aggregates. Defaults to 30. */
    windowDays?: number;
    audience?: "all" | "internal_only" | "internal_and_partner";
  } = {}): Promise<{
    items: ActionItemRow[];
    counts: { standing: number; event: number; total: number; returned: number };
    pagination: { limit: number; offset: number; windowDays: number };
  }> {
    const params: Record<string, string | number> = {};
    if (opts.domainId) params.domainId = opts.domainId;
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    if (opts.status) params.status = opts.status;
    if (opts.kind) params.kind = opts.kind;
    if (opts.windowDays !== undefined) params.windowDays = opts.windowDays;
    if (opts.audience) params.audience = opts.audience;
    return this.request("GET", "/api/action-items", { params });
  }

  async getActionItemCounts(opts: {
    domainId?: string;
    audience?: "all" | "internal_only" | "internal_and_partner";
  } = {}): Promise<{
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
    byKind: { insight: number; alert: number; task: number };
  }> {
    const params: Record<string, string> = {};
    if (opts.domainId) params.domainId = opts.domainId;
    if (opts.audience) params.audience = opts.audience;
    return this.request("GET", "/api/action-items/counts", { params });
  }

  // -------------------------------------------------------------------------
  // Notifications (Sources Spine — Phase 2 PR 22)
  //
  // Backs the notification bell + dropdown in the app shell header.
  // Source-of-truth is alert-kind action_items joined with the
  // per-user `action_item_user_reads` table (migration 0087). Read
  // state is per-user; team-level lifecycle (snooze, complete,
  // dismiss) still flows through /api/action-items.
  // -------------------------------------------------------------------------

  async getNotifications(opts: {
    /** 'active' (default) shows live alerts; 'all' includes resolved/snoozed for history. */
    status?: "active" | "all";
    /** 1..100, default 50. */
    limit?: number;
  } = {}): Promise<{ notifications: Notification[] }> {
    const params: Record<string, string | number> = {};
    if (opts.status) params.status = opts.status;
    if (opts.limit !== undefined) params.limit = opts.limit;
    return this.request("GET", "/api/notifications", { params });
  }

  async getNotificationCount(): Promise<{
    unread: number;
    capped: boolean;
    exactUnread: number;
  }> {
    return this.request("GET", "/api/notifications/count");
  }

  async markNotificationRead(id: string): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/notifications/${id}/read`);
  }

  async markAllNotificationsRead(): Promise<{
    success: boolean;
    markedRead: number;
  }> {
    return this.request("POST", "/api/notifications/mark-all-read");
  }

  // -------------------------------------------------------------------------
  // Known providers + Expected-sender baseline (Sources Spine — Phase 2 PR 23)
  //
  // Known providers is the global catalog the matcher uses to
  // identify Sources. The Configuration tab's "Expected Senders"
  // picker calls getKnownProviders to populate its dropdown, then
  // declareDomainVendor / undeclareDomainVendor to manage the
  // per-domain baseline. The audit-job emits
  // `vendor_drift_detected` action_items for Sources not in the
  // declared set, surfaced via the notification bell from PR 22.
  // -------------------------------------------------------------------------

  async getKnownProviders(opts: { search?: string } = {}): Promise<{
    providers: KnownProvider[];
  }> {
    const params: Record<string, string> = {};
    if (opts.search) params.search = opts.search;
    return this.request("GET", "/api/known-providers", { params });
  }

  async getDomainDeclaredVendors(
    domainId: string
  ): Promise<{ declaredVendors: DomainDeclaredVendor[] }> {
    return this.request("GET", `/api/domains/${domainId}/declared-vendors`);
  }

  async declareDomainVendor(
    domainId: string,
    body: { knownProviderId: string; notes?: string }
  ): Promise<{ declaredVendor: DomainDeclaredVendor | null }> {
    return this.request("POST", `/api/domains/${domainId}/declared-vendors`, {
      body,
    });
  }

  async undeclareDomainVendor(
    domainId: string,
    vendorId: string
  ): Promise<{ success: boolean }> {
    return this.request(
      "DELETE",
      `/api/domains/${domainId}/declared-vendors/${vendorId}`
    );
  }

  async updateActionItem(
    itemId: string,
    body: {
      status: "snoozed" | "completed" | "dismissed" | "not_applicable";
      snoozeDays?: number;
      reason?: string;
    }
  ): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/action-items/${itemId}`, { body });
    this.clearCacheByPattern("/api/action-items");
    return result;
  }

  async snoozeActionItem(itemId: string, days = 30): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/action-items/${itemId}/snooze`, { body: { days } });
    this.clearCacheByPattern("/api/action-items");
    return result;
  }

  async completeActionItem(itemId: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/action-items/${itemId}/complete`);
    this.clearCacheByPattern("/api/action-items");
    return result;
  }

  async dismissActionItem(itemId: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/action-items/${itemId}/dismiss`);
    this.clearCacheByPattern("/api/action-items");
    return result;
  }

  // ============================================
  // APEX Preview (post-migration-0081)
  // ============================================

  async previewApexLink(body: {
    domainId: string;
    sourceIds?: string[];
    providerIds?: string[];
    signingDomains?: string[];
    audience?: "all" | "internal_only" | "internal_and_partner";
  }): Promise<ApexPreviewResponse> {
    return this.request("POST", "/api/apex-links/preview", { body });
  }

  async getApexLinkPreview(linkId: string, audience?: "all" | "internal_only" | "internal_and_partner"): Promise<ApexPreviewResponse> {
    const params: Record<string, string> = {};
    if (audience) params.audience = audience;
    return this.request("GET", `/api/apex-links/${linkId}/preview`, { params });
  }
}

// Action Items (unified) types — match the OpenAPI ActionItem schema.
export interface ActionItemRow {
  id: string;
  organizationId: string;
  domainId: string | null;
  sourceId: string | null;
  /** `standing` rows are virtual; computed from spine state (PR 14). */
  kind: "insight" | "alert" | "task" | "standing";
  category: string;
  subject: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  confidence: "high" | "medium" | "low" | null;
  title: string;
  description: string;
  recommendations: unknown[];
  metadata: Record<string, unknown>;
  internalActionLink: string | null;
  partnerHandoff: string | null;
  isPartnerShareable: boolean;
  status: "active" | "snoozed" | "completed" | "dismissed" | "not_applicable";
  snoozedUntil: string | null;
  completedAt: string | null;
  createdAt: string;
  lastSeenAt: string;
  domainName: string | null;
}

/**
 * Notification row served by /api/notifications (Sources Spine — Phase 2 PR 22).
 *
 * Adjacent to ActionItemRow but with per-user `readByMe` baked in
 * (and lifecycle fields the bell doesn't need — completedAt,
 * partnerHandoff, isPartnerShareable — stripped to keep the payload
 * tight). `recommendations` and `metadata` come back as JSON-encoded
 * strings to match the shape of /api/action-items; clients that need
 * the structured form parse downstream.
 */
export interface Notification {
  id: string;
  kind: "insight" | "alert" | "task";
  category: string;
  subject: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  confidence: "high" | "medium" | "low" | null;
  title: string;
  description: string;
  /** JSON-encoded array; parse downstream if you need the structured form. */
  recommendations: string;
  /** JSON-encoded object; parse downstream. */
  metadata: string;
  internalActionLink: string | null;
  status: "active" | "snoozed" | "completed" | "dismissed" | "not_applicable";
  snoozedUntil: string | null;
  domainId: string | null;
  domainName: string | null;
  sourceId: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  /** ISO timestamp when the current user read this. Null = unread. */
  readAt: string | null;
  readByMe: boolean;
}

/**
 * Known providers catalog row (Sources Spine — Phase 2 PR 23).
 *
 * Mirrors the columns the picker dropdown needs from
 * `known_providers`. Detection patterns, remediation templates, etc.
 * stay server-side; the picker only renders name + category + logo.
 */
export interface KnownProvider {
  id: string;
  name: string;
  /** 'esp' | 'crm' | 'marketing' | 'transactional' | 'infrastructure' | 'unknown'. */
  category: string;
  logoUrl: string | null;
}

/**
 * Domain declared vendor row (Sources Spine — Phase 2 PR 23).
 *
 * The customer's expected-sender baseline. Drift from this set
 * surfaces as `vendor_drift_detected` action items via the
 * notification bell.
 */
export interface DomainDeclaredVendor {
  id: string;
  knownProviderId: string;
  providerName: string;
  providerCategory: string;
  providerLogoUrl: string | null;
  /** Hydrated user triple; null when the row's declared_by user was deleted. */
  declaredBy?: { id: string; name: string | null; email: string } | null;
  notes: string | null;
  declaredAt: string;
}

export interface ApexPreviewResponse {
  link_id?: string;
  domain: { id: string; name: string };
  audience: "all" | "internal_only" | "internal_and_partner";
  items: ActionItemRow[];
  count: number;
  hidden_internal_only_count: number;
  generated_at: string;
}

// Action Items Summary types
export interface ActionItemsSummary {
  counts: {
    /** Includes standing conditions. What the Domain Overview renders. */
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    /**
     * Excludes standing conditions — the count the notifications page will
     * actually show. Use this for anything that links there; `total` counts
     * rows that page deliberately filters out.
     */
    eventTotal: number;
    bySource: { insights: number; tasks: number; alerts: number };
  };
  items: Array<{
    id: string;
    source: "alert" | "task" | "insight";
    title: string;
    description: string;
    priority: "critical" | "high" | "medium" | "low";
    isCompleted: boolean;
    createdAt: string;
    actionLink: string | null;
    domainId: string | null;
    domainName: string | null;
  }>;
  hasMore: boolean;
}

// AI Recommendations types (Proactive AI Insights)
export interface AIRecommendation {
  type: string;
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  count: number;
  action: string;
  link: string;
  domains?: string[];
}

export interface AIPendingClassification {
  id: string;
  identifier: string;
  sourceName: string;
  providerName: string | null;
  domainName: string;
  domainId: string;
  totalMessages: number;
  currentStatus: string;
  suggestedCategory: string | null;
  suggestedSubtype: string | null;
  aiClassification: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  analyzedAt: string;
  requiresApproval: boolean;
}

export interface AIEnforcementInsight {
  id: string;
  domainId: string;
  domainName: string;
  canAdvance: boolean;
  confidence: string;
  reasoning: string;
  riskLevel: string;
  riskDescription: string;
  recommendations: string[];
  analyzedAt: string;
}

export interface AIRecentAction {
  id: string;
  domainName: string;
  sourceName: string | null;
  sourceIdentifier: string | null;
  triggerType: string;
  actionTaken: string;
  classification: string;
  confidence: string;
  createdAt: string;
}

export interface AIConfigIssue {
  type: "config_issue";
  priority: "high" | "medium";
  sourceId: string;
  sourceIdentifier: string;
  sourceName: string;
  domainName: string;
  domainId: string;
  totalMessages: number;
  issues: string[];
  recommendation: string;
}

export interface AICachedInsight {
  id: string;
  domainId: string | null;
  sourceId: string | null;
  type: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  metadata: Record<string, unknown>;
  recommendations: string[];
  actionLink: string | null;
  confidence: string;
  createdAt: string;
  status?: "active" | "snoozed" | "resolved" | "not_applicable";
  snoozedUntil?: string | null;
}

export interface AIInsightSuppression {
  id: string;
  domainId: string | null;
  domainName?: string | null;
  insightType: string;
  fingerprint: string;
  createdAt: string;
}

export interface AIRecommendationsResponse {
  available: boolean;
  tier: string;
  recommendations: AIRecommendation[];
  pendingClassifications: AIPendingClassification[];
  enforcementInsights: AIEnforcementInsight[];
  recentActions: AIRecentAction[];
  configIssues: AIConfigIssue[];
  insights?: AICachedInsight[];
  generatedAt: string;
}

// MSP Client Organization type
export interface MspClientOrganization extends Organization {
  domain_count?: number;
  dmarc_pass_rate?: number;
  enforcement_percentage?: number;
  last_report_at?: string | null;
  rate_cents_per_domain?: number;
  minimum_monthly_cents?: number;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
}

// MSP Invoice types
export interface MspInvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

export type MspInvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "cancelled";

export interface MspInvoice {
  id: string;
  msp_organization_id: string;
  client_organization_id: string;
  client_name?: string;
  client_email?: string;
  invoice_number?: string;
  period_start: string;
  period_end: string;
  line_items: MspInvoiceLineItem[] | string; // JSON string from DB or parsed array
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: MspInvoiceStatus;
  notes?: string | null;
  pdf_key?: string | null;
  email_sent_to?: string | null;
  email_sent_from?: string | null;
  email_message_id?: string | null;
  external_invoice_id?: string | null;
  external_invoice_url?: string | null;
  created_at: string;
  sent_at?: string | null;
  paid_at?: string | null;
  due_date?: string | null;
}

// MSP Email Domain types
export interface MspDnsRecord {
  type: string;
  name: string;
  value: string;
  priority?: number;
  ttl?: number;
  status?: "pending" | "verified" | "not_started";
}

export interface MspEmailDomain {
  id: string;
  msp_organization_id: string;
  domain: string;
  resend_domain_id?: string | null;
  status: "pending" | "verified" | "failed";
  dns_records: MspDnsRecord[] | string; // JSON string from DB or parsed array
  default_from_email?: string | null;
  default_from_name?: string | null;
  created_at: string;
  verified_at?: string | null;
  last_checked_at?: string | null;
}

// MSP App Domain (custom white-label domain for the web app)
export interface SslValidationRecord {
  txt_name: string;
  txt_value: string;
}

export interface MspAppDomain {
  id: string;
  msp_organization_id?: string;
  domain: string;
  status: "pending" | "verified" | "failed";
  dns_records: MspDnsRecord[] | string;
  ssl_status: "pending" | "active" | "expired" | null;
  cf_custom_hostname_id?: string | null;
  cf_ssl_status?: string | null;
  ssl_validation_records?: SslValidationRecord[] | string;
  is_primary: boolean | number;
  created_at: string;
  verified_at?: string | null;
  updated_at?: string;
}

// MSP Branding configuration
export interface MspBrandingConfig {
  logo_url?: string | null;
  favicon_url?: string | null;
  primary_color?: string;
  accent_color?: string;
  company_name?: string | null;
  support_email?: string | null;
  support_url?: string | null;
  custom_css?: string | null;
}

// Domain Connect template type for API responses
export interface DomainConnectTemplate {
  providerId: string;
  serviceId: string;
  name: string;
  description: string;
  variableDescription?: string;
  syncPubKeyDomain: string;
  syncBlock: boolean;
  shared: boolean;
  multiInstance: boolean;
  version: number;
  logoUrl?: string;
  variables?: Array<{ name: string; description: string; required: boolean; default?: string }>;
  records: Array<{ type: string; host: string; data?: string; ttl?: number }>;
}

// MSP Team Management types
export interface MspTeamMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_msp_admin: boolean | number; // SQLite returns 0/1
  assigned_client_count?: number;
  created_at: string;
  last_login_at?: string | null;
}

export interface MspClientAssignment {
  id: string;
  client_organization_id: string;
  client_name: string;
  access_level: "full" | "readonly" | "billing_only";
  assigned_by_user_id?: string;
  assigned_by_name?: string;
  created_at: string;
}

// SSO Configuration types
export type SsoProviderType = "saml" | "oidc";
export type SsoProviderName = "okta" | "azure_ad" | "google" | "onelogin" | "custom";

export interface SsoConfiguration {
  id: string;
  provider_type: SsoProviderType;
  provider_name?: SsoProviderName | string;
  // SAML settings
  idp_entity_id?: string;
  idp_sso_url?: string;
  idp_slo_url?: string;
  // OIDC settings
  oidc_issuer?: string;
  oidc_client_id?: string;
  oidc_scopes?: string;
  // SP settings
  sp_entity_id: string;
  sp_acs_url: string;
  // Mappings
  attribute_mapping: Record<string, string>;
  role_mapping: Record<string, string[]>;
  default_role: string;
  msp_group_mapping: Record<string, { client_id: string; access_level: string }>;
  // Behavior
  jit_provisioning: boolean;
  enforce_sso: boolean;
  allow_idp_initiated: boolean;
  enabled: boolean;
  verified_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SsoEmailDomain {
  id: string;
  email_domain: string;
  verified: boolean;
  verified_at?: string | null;
  verification_token?: string;
}

export interface SsoLoginAttempt {
  id: string;
  provider_type: SsoProviderType;
  status: "success" | "failed" | "pending";
  error_code?: string;
  error_message?: string;
  email?: string;
  name?: string;
  groups?: string[];
  created_user: boolean;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export const api = new ApiClient();
